import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { isDebitCompliant, COMPLIANCE_MESSAGE } from '../utils/financialCompliance';
import { resolveFileUrl } from '../utils/fileUrl';
import { parseProofs, serializeProofs, isImageUrl, MAX_PROOFS } from '../utils/proofs';
import ProofCell from '../components/ProofCell';
import { colors, spacing, fontSize, fontWeight, radius } from '../styles/theme';
import SearchableSelect, { SearchableSelectOption } from '../components/SearchableSelect';
import { ResizableColumnsProvider, ResizableTh, useTableWidth } from '../components/ResizableColumns';
import FloatingHScrollbar from '../components/FloatingHScrollbar';
import ColumnFilter, { BLANK_VALUE } from '../components/ColumnFilter';
import DayRangeCalendar from '../components/DayRangeCalendar';
import { downloadLedgerExcel, downloadLedgerPdf } from '../utils/ledgerExport';
import ReorderLedgersModal from '../components/ReorderLedgersModal';
import { INCOME_ENTRY_ENABLED, RETURN_ENTRY_ENABLED } from '../constants/featureFlags';
import LedgerDownloadDialog from '../components/LedgerDownloadDialog';
import { apiErrorMessage } from '../utils/apiError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Styles ───────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: { padding: '20px 32px', background: colors.surfaceMuted, minHeight: '100vh', fontFamily: 'system-ui, sans-serif', boxSizing: 'border-box' },

  // Header row
  headerBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing(4), gap: spacing(4) },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: colors.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: spacing(3) },
  filterBadge: { background: colors.primaryBg2, color: colors.primaryDark, fontSize: fontSize.sm, padding: '0.2rem 0.6rem', borderRadius: radius.sm, fontWeight: fontWeight.medium },
  subtitle: { fontSize: fontSize.md, color: colors.textMuted, marginTop: spacing(1) },

  // Toolbar
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing(4), gap: spacing(3), flexWrap: 'wrap' },
  toolbarLeft: { display: 'flex', gap: spacing(2), alignItems: 'center', flexWrap: 'wrap' },
  toolbarRight: { display: 'flex', gap: spacing(2), alignItems: 'center' },

  // Controls
  select: { padding: '0.5rem 0.75rem', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: fontSize.md, background: colors.surface, outline: 'none', color: colors.textSecondary },
  searchInput: { padding: '0.5rem 0.75rem', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: fontSize.md, background: colors.surface, outline: 'none', minWidth: '240px', color: colors.textSecondary },
  btnGhost: { background: colors.surface, border: `1px solid ${colors.border}`, color: colors.textSecondary, padding: '0.5rem 1rem', borderRadius: radius.sm, fontSize: fontSize.md, fontWeight: fontWeight.semibold, cursor: 'pointer' },
  btnGroup: { display: 'flex', borderRadius: radius.sm, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  btnIncome: { background: '#10b981', color: 'white', border: 'none', padding: '0.5rem 1.1rem', fontSize: fontSize.md, fontWeight: fontWeight.semibold, cursor: 'pointer', borderRight: '1px solid #047857' },
  btnExpense: { background: colors.primary, color: 'white', border: 'none', padding: '0.5rem 1.1rem', fontSize: fontSize.md, fontWeight: fontWeight.semibold, cursor: 'pointer' },

  // Table
  // overflowX auto lets the table scroll horizontally when it can't fit (many
  // columns / zoomed in) instead of clipping the right-hand columns. When it
  // does fit, no scrollbar shows.
  // width: fit-content shrinks the white card to the table's own width. A
  // plain block div would stay full-page-width, painting white to the right of
  // a table that's narrower than the page (the table is sized by its columns,
  // not the container). maxWidth 100% keeps it from overflowing the page when
  // the table is wider, so overflowX still scrolls.
  tableWrap: { background: colors.surface, borderRadius: radius.xl, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: `1px solid ${colors.border}`, overflowX: 'auto', width: 'fit-content', maxWidth: '100%' },
  // No width here on purpose — the call site sets width: max-content so the
  // table is exactly the sum of its column widths. A width: 100% would make
  // the browser spread leftover container width across the columns (under
  // table-layout: fixed), which both overrides the user's saved widths and
  // pools the surplus in the last column.
  table: { borderCollapse: 'collapse', textAlign: 'center', fontSize: fontSize.md },
  // The padding here is lifted onto an inner div by ResizableTh / TextCell —
  // left on the cell it would act as a hard minimum width (a cell can't be
  // narrower than its own horizontal padding), stopping columns from being
  // dragged below ~33px. Cells written as plain <td> keep it inline and simply
  // don't shrink as far.
  // Vertical padding is deliberately a step above the body rows' 0.7rem: at
  // matched padding the header lands on the same ~43px as a data row and the
  // band reads flat against the table. 0.85rem puts it at ~46px.
  th: { padding: '0.85rem 1rem', fontWeight: 700, color: '#1a3a5c', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `2px solid ${colors.border}`, borderRight: `1px solid ${colors.border}`, background: '#86afd8', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'center' },
  td: { padding: '0.7rem 1rem', borderBottom: `1px solid ${colors.borderMuted}`, borderRight: `1px solid ${colors.borderMuted}`, color: colors.textSecondary, verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' },
  tdRight: { padding: '0.7rem 1rem', borderBottom: `1px solid ${colors.borderMuted}`, borderRight: `1px solid ${colors.borderMuted}`, textAlign: 'center', fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums', overflow: 'hidden', whiteSpace: 'nowrap' },
  input: { width: '100%', padding: '0.35rem 0.5rem', borderRadius: radius.sm - 2, border: `1px solid ${colors.border}`, fontSize: fontSize.md, boxSizing: 'border-box' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.2rem' },
  typeTag: { fontSize: '0.65rem', fontWeight: fontWeight.bold, padding: '1px 6px', borderRadius: radius.sm - 2, marginRight: spacing(1), letterSpacing: '0.03em' },

  actionBtnEdit: { background: 'linear-gradient(135deg,#0ea5e9,#2563eb)', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' },
  actionBtnDelete: { background: 'linear-gradient(135deg,#f43f5e,#e11d48)', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' },
  addRowBtn: { background: colors.primaryBg2, color: colors.primaryDark, border: 'none', borderRadius: radius.pill, width: '18px', height: '18px', lineHeight: '18px', fontSize: '0.75rem', fontWeight: fontWeight.bold, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  typeToggle: { border: 'none', borderRadius: radius.sm - 2, padding: '1px 6px', fontSize: '0.65rem', fontWeight: fontWeight.bold, cursor: 'pointer', background: colors.surfaceMuted, color: colors.textMuted, letterSpacing: '0.03em' },
  typeToggleIn: { background: colors.successBg, color: colors.successDark },
  typeToggleOut: { background: colors.dangerBg2, color: colors.dangerDark },

  emptyState: { padding: '3rem', textAlign: 'center', color: colors.textFaint },

  // Collapsible filter panel. A grid of equal-width fields rather than a row of
  // native selects: a <select> sizes itself to its longest option, which is why
  // an inline row of them came out ragged — and it can't do multi-select or a
  // search box, which is why ColumnFilter renders the control itself.
  filterPanel: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.xl, padding: spacing(4), marginBottom: spacing(4), boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  // 240 rather than 180: the subledger columns hold vehicle numbers with the
  // model in brackets, and at 180 both the trigger and its menu clipped them.
  filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: spacing(3) },
  filterField: { display: 'flex', flexDirection: 'column', gap: spacing(1), minWidth: 0 },
  filterLabel: { fontSize: '0.68rem', fontWeight: fontWeight.semibold, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' },
  // Boxed to the same height as a ColumnFilter trigger so the panel's grid
  // stays even with a pair of checkboxes sitting in one of its cells.
  pettyBoxes: { display: 'flex', flexDirection: 'column', gap: '2px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, padding: '0.2rem 0.6rem' },
  pettyBox: { display: 'flex', alignItems: 'center', gap: spacing(2), fontSize: fontSize.sm, fontWeight: fontWeight.medium, cursor: 'pointer', whiteSpace: 'nowrap' },
  // Two halves of one control, boxed to the same height as a ColumnFilter
  // trigger so the panel's grid stays even.
  downloadBox: { display: 'flex', alignItems: 'stretch', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, overflow: 'hidden' },
  downloadBtn: { flex: 1, background: 'none', border: 'none', padding: '0.45rem 0.3rem', cursor: 'pointer', color: colors.primary, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, fontFamily: 'inherit' },
  downloadBtnOff: { color: colors.textFaint, cursor: 'default' },
  downloadDivider: { width: 1, background: colors.border },

  // Subtotal chip in the toolbar — label stacked over the figure so it reads as
  // a labelled statistic rather than another control.
  subtotalBox: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15, padding: '0.25rem 0.75rem', borderRight: `1px solid ${colors.border}`, marginRight: spacing(1) },
  subtotalLabel: { fontSize: '0.65rem', fontWeight: fontWeight.semibold, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' },
  subtotalValue: { fontSize: fontSize.md, fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },

  // Delete-confirmation modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: spacing(4) },
  modalCard: { background: colors.surface, borderRadius: radius.xl, boxShadow: '0 20px 40px rgba(0,0,0,0.25)', padding: spacing(5), width: '100%', maxWidth: '460px', boxSizing: 'border-box' },
  modalTitle: { margin: 0, fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary },
  modalBody: { fontSize: fontSize.md, color: colors.textSecondary, lineHeight: 1.5, marginTop: spacing(2) },
  modalSampleBox: { marginTop: spacing(3), border: `1px solid ${colors.border}`, borderRadius: radius.md, maxHeight: '180px', overflowY: 'auto', fontSize: fontSize.sm },
  modalSampleRow: { display: 'flex', alignItems: 'center', padding: '6px 10px', borderBottom: `1px solid ${colors.borderMuted}`, color: colors.textSecondary },
};

// Highest Subledger slot the UI will show. The backend still supports and
// stores slots up to 5 — this only hides 4 and 5 from the workspace, the Add
// Expense row and the on-screen Excel/PDF export, at the client's request.
//
// Deliberately a cap rather than a deletion: nothing is dropped server-side,
// existing entries keep their Subledger 4/5 values, and restoring the columns
// is a one-line change back to 5 (plus re-enabling the two commented-out
// columns in LedgerDownloadDialog's EXPORT_COLUMNS). The client has changed
// their mind on this before, so the path back is kept short on purpose.
const VISIBLE_SUBLEDGER_SLOT_MAX = 3;

// Particulars is hidden from the workspace at the client's request, the same
// way Subledger 4/5 are capped above — and deliberately NOT deleted:
//
//   * The column still exists and every stored description is intact. 23 of the
//     32 entries had one when this was hidden ("Direct UPI Transfer To Pump",
//     "Fuel Allowance - ...") and none of them were touched.
//   * `draft.particular` is still seeded from the row being edited and still
//     rides along in the save payload. That is load-bearing: update_transaction
//     applies `exclude_unset=True`, so a field the UI keeps sending unchanged is
//     written back identically, while DROPPING it from the draft would send
//     `particular: ''` and silently blank the description on every edit.
//   * Nothing matches on its value anywhere (no '[auto]' markers, no LIKE, no
//     comparisons), so hiding it changes no behaviour beyond display.
//
// To restore: re-add the <ResizableTh>, the <TextCell>, the draft-row
// <SearchableSelect>, the { id: 'particulars' } width entry, put the column
// count back to 13, and re-enable the two lines in LedgerDownloadDialog's
// EXPORT_COLUMNS / ESSENTIAL_COLUMN_IDS.

// What typing in the Amount field does to the draft.
//
// The sign is a shortcut for the entry's type: a leading "+" makes the entry a
// return (CREDIT), "-" an expense (DEBIT). The typed sign is KEPT in the field
// so what you typed is what you see — it's stripped at save time
// (amountToSave), because amounts are stored as a positive magnitude with
// transaction_type carrying the direction (see how rows render, and
// get_petty_cash_summary on the backend).
//
// Anything that isn't a sign, digits or a single dot is dropped, so a stray
// letter can't sit in the field looking like it will save. A lone "+" or "-"
// is allowed to stand while typing — it sets the type and leaves the amount
// otherwise empty, rather than the field refusing the first keystroke.
const amountDraftPatch = (raw: string, draft: any): Record<string, any> => {
  const cleaned = raw.replace(/[^0-9.+-]/g, '');
  const sign = cleaned.startsWith('+') ? 'CREDIT' : cleaned.startsWith('-') ? 'DEBIT' : null;
  // Signs only mean anything in front, and only the first dot survives, so
  // "1.2.3" or "5+0" can't be typed into something ambiguous.
  const digits = cleaned.replace(/[+-]/g, '').replace(/\.(?=.*\.)/g, '');
  const prefix = sign === 'CREDIT' ? '+' : sign === 'DEBIT' ? '-' : '';
  return {
    amount: prefix + digits,
    ...(sign ? { transaction_type: sign } : {}),
  };
};

// The number to persist: the field may carry a leading +/- for display, but
// the stored amount is always the magnitude.
const amountToSave = (raw: any): number => Math.abs(parseFloat(String(raw ?? '')) || 0);

// Fixed px width of the trailing "+ add Subledger column" header cell.
const PLUS_COL_WIDTH = 30;

// A bare safety floor, not a layout. The table is sized by the sum of its
// column widths, so any larger value here would stop the table shrinking as
// you drag columns in — the width you reclaimed would sit there as a blank gap
// on the right instead of closing up. This only guards against the table
// collapsing to nothing if stored widths are ever corrupted; the column
// widths, not this, decide how wide the table is.
const TABLE_MIN_WIDTH = 300;

// Entries *not* drawn from the petty-cash drawer are tinted blue end to end,
// so where the money came from reads off the row itself instead of costing a
// column. Petty-cash rows are left exactly as they were — dark text with the
// red/green amount — since they're the ones being reconciled against the
// drawer and shouldn't change appearance.
const NON_PETTY_COLOR = colors.primary;

// The petty-cash tick boxes live in columnFilters alongside every other
// filter, so "Clear filters" and the Filters badge pick them up for free.
// Neither box ticked — and both ticked — mean "no filter", matching the "All"
// the dropdowns default to.
const PETTY_FILTER_ID = 'pettyCash';
type PettyChoice = 'PETTY' | 'NON_PETTY';

// Expenses / returns, also in columnFilters so the badge and "Clear filters"
// pick it up for free. Radio-style rather than tick boxes: the three choices
// are mutually exclusive, and "All" is the absence of a filter — it stores
// nothing, so it can never count towards the active-filter badge.
const KIND_FILTER_ID = 'entryKind';
type KindChoice = 'EXPENSE' | 'RETURN';

// The day-range filter rides in columnFilters too, for the same reason: stored
// as [from, to] (either half may be ''), so the Filters badge counts it and
// "Clear filters" wipes it without either needing to know about dates. The
// range narrows *within* the month already chosen in the toolbar.
const DATE_FILTER_ID = 'dateRange';

// transaction_date arrives as 'YYYY-MM-DD' or a full ISO stamp; the <input
// type="date"> values are always 'YYYY-MM-DD', so trimming the time makes the
// two directly comparable as strings.
const dayOf = (d: any) => (d ? String(d).split('T')[0] : '');

const prettyDay = (d: string) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// Every cell is nowrap + ellipsis, so any value wider than its (resizable)
// column gets visually clipped — "KA 51 JG 9960..." tells you nothing useful.
//
// The tooltip is portaled to <body> rather than positioned inside the cell:
// the table wrapper is overflow:auto, so an in-flow tooltip would be clipped by
// the very container we're trying to escape. It only appears when the text is
// genuinely cut off (scrollWidth > clientWidth), so hovering a short value
// doesn't flash a redundant bubble.
//
// Declared at module scope so React doesn't remount every cell each render.
// The <table>, sized to exactly the sum of its column widths.
//
// It has to be an explicit pixel width, and it has to be read from inside the
// ResizableColumnsProvider — hence the separate component. `width: max-content`
// is the obvious-looking alternative and is wrong: it makes the browser floor
// each column at its longest unwrapped content, so a column dragged down to
// 8px silently renders at ~136px and the reclaimed space never comes back.
function SizedTable({ columns, extraWidth = 0, children }: {
  columns: { id: string; defaultWidth: number }[];
  // Width of any fixed, non-resizable columns in the header (the "+" cell),
  // which aren't in `columns` but still take up room.
  extraWidth?: number;
  children: React.ReactNode;
}) {
  const width = useTableWidth(columns) + extraWidth;
  return (
    <table style={{ ...s.table, tableLayout: 'fixed', width, minWidth: TABLE_MIN_WIDTH }}>
      {children}
    </table>
  );
}

function TextCell({ value, style, placeholder = '-' }: {
  value?: string | null;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const cellRef = useRef<HTMLTableCellElement>(null);
  // The padded inner div is what actually clips the text, so truncation is
  // measured on it rather than on the cell.
  const innerRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ left: number; top: number; below: boolean } | null>(null);

  const text = value == null || value === '' ? placeholder : String(value);
  const isEmpty = text === placeholder;

  const show = () => {
    const el = cellRef.current;
    const inner = innerRef.current;
    if (!el || !inner || isEmpty) return;
    if (inner.scrollWidth <= inner.clientWidth + 1) return;   // not actually truncated
    const r = el.getBoundingClientRect();
    // Flip below the cell when there isn't room above it.
    const below = r.top < 44;
    setTip({ left: r.left + r.width / 2, top: below ? r.bottom + 6 : r.top - 6, below });
  };

  // Padding lives on the inner div, not the cell: on the cell it would floor
  // the column's width (see ResizableTh), here it just gets clipped.
  const { padding, paddingLeft, paddingRight, paddingTop, paddingBottom, ...cellStyle } = { ...s.td, ...style };

  return (
    <td
      ref={cellRef}
      style={{ ...cellStyle, padding: 0 }}
      onMouseEnter={show}
      onMouseLeave={() => setTip(null)}
    >
      <div ref={innerRef} style={{ padding, paddingLeft, paddingRight, paddingTop, paddingBottom, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text}
      </div>
      {tip && createPortal(
        <div style={{
          position: 'fixed',
          left: tip.left,
          top: tip.top,
          transform: `translate(-50%, ${tip.below ? '0' : '-100%'})`,
          maxWidth: 380,
          padding: '6px 10px',
          borderRadius: radius.sm,
          background: '#0f172a',
          color: '#f8fafc',
          fontSize: '0.78rem',
          lineHeight: 1.35,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          boxShadow: '0 6px 18px rgba(15,23,42,0.28)',
          pointerEvents: 'none',   // never swallow the row's click-to-edit
          zIndex: 10050,
        }}>
          {text}
        </div>,
        document.body
      )}
    </td>
  );
}

export default function LedgerWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once at mount — these seed initial state (incl. deep links like
  // ?category=X from the P&L screen's "View Ledger" buttons). Subsequent
  // changes flow the other way: state -> URL, via the effect below.
  const [urlMonth] = useState(() => parseInt(searchParams.get('month') || String(new Date().getMonth() + 1)));
  const [urlYear] = useState(() => parseInt(searchParams.get('year') || String(new Date().getFullYear())));
  const [urlCategory] = useState(() => searchParams.get('category') || '');

  const [transactions, setTransactions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [transferredFromOptions, setTransferredFromOptions] = useState<any[]>([]);
  const [senderOptions, setSenderOptions] = useState<any[]>([]);
  const [receiverOptions, setReceiverOptions] = useState<any[]>([]);
  // Particulars reuses the same ledger_party_options table under its own kind,
  // so it needed no schema change. Unlike the party columns it isn't an FK —
  // the transaction still stores the text, so this list is a picker that keeps
  // wording consistent, not a source of truth for past entries.
  // Remarks works exactly like Particulars: a picker over its own kind, while
  // the transaction still stores free text. Keeping it free text is what lets
  // an operator type a one-off reason — and what keeps every remark already
  // saved before this list existed readable.
  const [remarkOptions, setRemarkOptions] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  // Subledger 2-5 — "Subledger 1" above is the existing subcategories system.
  // activeSlots lists which extra columns have been revealed (shared
  // workspace-wide); subledgerValues holds each active slot's own distinct
  // value list, fetched lazily as slots become active.
  const [activeSlots, setActiveSlots] = useState<number[]>([]);
  const [subledgerValues, setSubledgerValues] = useState<Record<number, any[]>>({});
  
  const [month, setMonth] = useState(urlMonth);
  const [year, setYear] = useState(urlYear);
  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<any>({});
  // Snapshot taken when the editor opens, so Escape / click-away can tell an
  // untouched row (close silently) from one with real edits (prompt first).
  const [originalDraft, setOriginalDraft] = useState<any>(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  // Per-column value filters, keyed by the same column ids used for resizing.
  // An absent/empty entry means "no filter on that column", so a column whose
  // values grow later doesn't silently start excluding the new ones.
  // Ledger filtering lives in here alongside every other column rather than in
  // its own state, so all seven dropdowns behave identically. The ?category=
  // deep link from the P&L's "View Ledger" button only seeds it at mount.
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    () => (urlCategory ? { ledgerName: [urlCategory] } : ({} as Record<string, string[]>))
  );
  const setLedgerFilter = (names: string[]) =>
    setColumnFilters(prev => ({ ...prev, ledgerName: names }));
  const pettyPicked = (columnFilters[PETTY_FILTER_ID] || []) as PettyChoice[];
  const togglePetty = (choice: PettyChoice) =>
    setColumnFilters(prev => {
      const cur = prev[PETTY_FILTER_ID] || [];
      return {
        ...prev,
        [PETTY_FILTER_ID]: cur.includes(choice) ? cur.filter(v => v !== choice) : [...cur, choice],
      };
    });

  // Empty means "All" — the default, and what "Clear filters" restores.
  const kindPicked = (columnFilters[KIND_FILTER_ID] || [])[0] as KindChoice | undefined;
  const setKind = (choice: KindChoice | null) =>
    setColumnFilters(prev => ({ ...prev, [KIND_FILTER_ID]: choice ? [choice] : [] }));

  const [dateFrom = '', dateTo = ''] = columnFilters[DATE_FILTER_ID] || [];
  // Both halves blank stores nothing at all, so an emptied range stops counting
  // towards the Filters badge instead of lingering as a filter that filters
  // nothing.
  // Stable identity: the calendar keeps a window mouse-up listener keyed to
  // this, and a fresh function every render would churn it mid-drag.
  const setDateRange = useCallback((from: string, to: string) => {
    setColumnFilters(prev => ({ ...prev, [DATE_FILTER_ID]: from || to ? [from, to] : [] }));
  }, []);

  // A range from the month just left would hide every row in the month just
  // arrived at, and the panel is usually shut when they switch — so it looks
  // like an empty month rather than a filter. Drop it on the way through.
  useEffect(() => {
    setColumnFilters(prev => (prev[DATE_FILTER_ID]?.length ? { ...prev, [DATE_FILTER_ID]: [] } : prev));
  }, [month, year]);
  // Rows picked out by dragging, for the selection subtotal.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Oldest-first by default: the client reads the ledger the way the month ran,
  // so entries should climb 01 → 31 rather than start at the latest.
  const [sortOrder, setSortOrder] = useState<'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc' | 'sl-asc'>('date-asc');
  // 'ledger' groups every entry under one ledger together (then by subledger);
  // 'date' is the plain chronological read, where sortOrder alone decides the
  // order. In 'ledger' mode sortOrder only orders rows inside a leaf group.
  const [groupBy, setGroupBy] = useState<'ledger' | 'date'>('ledger');
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const tableWrapRef = useRef<HTMLDivElement>(null);

  // ── Rename / delete of ledger & subledger names (from the dropdowns) ──
  type DeleteKind = 'category' | 'subcategory' | 'subledger';
  interface DeleteTarget { kind: DeleteKind; slot?: number; option: SearchableSelectOption; }
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteUsage, setDeleteUsage] = useState<{ count: number; sample: any[] } | null>(null);
  const [deleteUsageLoading, setDeleteUsageLoading] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  // Single-entry delete — password-gated the same way a ledger delete is.
  const [deleteEntryTarget, setDeleteEntryTarget] = useState<any | null>(null);
  const [deleteEntryPassword, setDeleteEntryPassword] = useState('');
  const [deletingEntry, setDeletingEntry] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const DELETE_LABEL: Record<DeleteKind, string> = {
    category: 'ledger name',
    subcategory: 'Subledger 1 value',
    subledger: 'subledger value',
  };
  const usageBasePath = (t: DeleteTarget) =>
    t.kind === 'category' ? `categories/${t.option.id}`
    : t.kind === 'subcategory' ? `subcategories/${t.option.id}`
    : `subledger-values/${t.option.id}`;

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [txRes, catRes, subRes, tfRes, sndRes, rcvRes, remRes] = await Promise.all([
        axios.get(`${API}/api/financial/transactions?month=${month}&year=${year}`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/categories`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/subcategories`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=TRANSFERRED_FROM`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=SENDER`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=RECEIVER`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=REMARK`, { headers: getAuthHeader() })
      ]);

      const dataArray = Array.isArray(txRes.data) ? txRes.data : (txRes.data.data || []);
      setTransactions(dataArray);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
      setTransferredFromOptions(tfRes.data);
      setSenderOptions(sndRes.data);
      setReceiverOptions(rcvRes.data);
      setRemarkOptions(remRes.data);
    } catch (error) {
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAllData(); }, [month, year]);

  const fetchSubledgerValues = async (slot: number) => {
    const res = await axios.get(`${API}/api/financial/subledger-values`, { params: { slot }, headers: getAuthHeader() });
    setSubledgerValues(prev => ({ ...prev, [slot]: res.data }));
  };

  const fetchSubledgerSlots = async () => {
    try {
      const res = await axios.get(`${API}/api/financial/subledger-slots`, { headers: getAuthHeader() });
      const slots: number[] = (res.data || [])
        .map((s: any) => s.slot)
        .filter((slot: number) => slot <= VISIBLE_SUBLEDGER_SLOT_MAX)
        .sort((a: number, b: number) => a - b);
      setActiveSlots(slots);
      await Promise.all(slots.map(fetchSubledgerValues));
    } catch {
      // Subledger columns are an enhancement on top of the core ledger —
      // fail quietly so a hiccup here doesn't block the page from loading.
    }
  };

  useEffect(() => { fetchSubledgerSlots(); }, []);

  const MAX_SUBLEDGER_SLOT = VISIBLE_SUBLEDGER_SLOT_MAX;

  const addSubledgerColumn = async () => {
    try {
      const res = await axios.post(`${API}/api/financial/subledger-slots`, {}, { headers: getAuthHeader() });
      if (res.data?.slot) {
        toast.success(`Subledger ${res.data.slot} added`);
        await fetchSubledgerSlots();
      } else {
        toast.error(`All ${MAX_SUBLEDGER_SLOT} Subledger columns are already active`);
      }
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to add Subledger column'));
    }
  };

  // Only succeeds server-side if no transaction has ever been tagged with
  // this slot — guards against removing a column that's actually in use,
  // while letting a mistakenly-added empty column be cleaned up.
  const removeSubledgerColumn = async (slot: number) => {
    if (!window.confirm(`Remove Subledger ${slot}? This also deletes any values defined under it.`)) return;
    try {
      await axios.delete(`${API}/api/financial/subledger-slots/${slot}`, { headers: getAuthHeader() });
      toast.success(`Subledger ${slot} removed`);
      await fetchSubledgerSlots();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, `Failed to remove Subledger ${slot}`));
    }
  };

  // Attaches to the level above: slot 2 to the Subledger 1 row, slots 3-5 to
  // the value one slot up.
  const addNewSubledgerValueFor = (slot: number) => async (name: string): Promise<SearchableSelectOption | null> => {
    const parentId = slot === 2
      ? draft.ledger_subcategory_id
      : draft[`ledger_subledger_${slot - 1}_id`];
    if (!parentId) {
      toast.error(slot === 2 ? 'Pick a Subledger 1 first' : `Pick a Subledger ${slot - 1} first`);
      return null;
    }
    try {
      const res = await axios.post(
        `${API}/api/financial/subledger-values`,
        {
          slot,
          name,
          ...(slot === 2
            ? { parent_subcategory_id: Number(parentId) }
            : { parent_value_id: Number(parentId) }),
        },
        { headers: getAuthHeader() },
      );
      await fetchSubledgerValues(slot);
      toast.success(`Added under ${parentNameForSlot(slot) || `Subledger ${slot - 1}`}`);
      return { id: res.data.id, name: res.data.name };
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to add value'));
      return null;
    }
  };

  // Keep the URL in sync with the selected period/filter so a page refresh
  // (or copy-pasting the link) preserves what's currently being viewed. The
  // deep-linked ?category= was already read into columnFilters at mount, so the
  // first run just writes the same value back rather than stripping it.
  //
  // ?category= only carries a single ledger, so it's written when exactly one
  // is picked and omitted otherwise — better a link that drops a multi-select
  // than one that silently claims a narrower filter than you had.
  const ledgerParam = (columnFilters['ledgerName'] || []).length === 1
    ? columnFilters['ledgerName'][0]
    : '';
  useEffect(() => {
    const params: Record<string, string> = { month: String(month), year: String(year) };
    if (ledgerParam) params.category = ledgerParam;
    setSearchParams(params, { replace: true });
  }, [month, year, ledgerParam, setSearchParams]);

  // Single entry point for opening the editor, so the dirty-check baseline can
  // never drift out of sync with what was actually loaded into the draft.
  const beginEdit = (id: number | 'new', initial: any) => {
    setEditingId(id);
    setDraft(initial);
    setOriginalDraft(initial);
  };

  const closeEdit = () => {
    setEditingId(null);
    setOriginalDraft(null);
    setUnsavedPrompt(false);
  };

  const startNewEntry = (type: 'CREDIT' | 'DEBIT') => {
    beginEdit('new', {
      transaction_date: `${year}-${String(month).padStart(2, '0')}-01`,
      transaction_type: type,
      ledger_category_id: '',
      ledger_subcategory_id: '',
      particular: '',
      amount: '',
      payment_mode: 'UPI',
      paid_from_petty_cash: false,
      transferred_from_id: '',
      sender_id: '',
      receiver_id: '',
      remarks: '',
      proof: null
    });
  };

  const startEdit = (tx: any) => {
    beginEdit(tx.id, {
      ...tx,
      transaction_date: tx.transaction_date ? tx.transaction_date.split('T')[0] : '',
      // Show a return's amount with its "+" so the field reads the same way it
      // would if you'd typed it. Expenses stay bare — an unsigned amount is
      // already an expense, and prefixing every one with "-" would be noise.
      amount: (tx.transaction_type === 'CREDIT' ? '+' : '') + String(tx.amount)
    });
  };

  // Adds another entry under the same date & category as an existing row —
  // e.g. a second "Software" line item on 18/06 — without re-picking either
  // from scratch. Subcategory is left blank since that's typically what's
  // different about the new line; transaction_type defaults to the source
  // row's type but stays editable via the IN/OUT toggle in the edit row.
  const startNewEntryFor = (tx: any) => {
    beginEdit('new', {
      transaction_date: tx.transaction_date ? tx.transaction_date.split('T')[0] : `${year}-${String(month).padStart(2, '0')}-01`,
      // Defaults to DEBIT rather than inheriting the source row's type: "+" is
      // for adding another expense under the same ledger, so a CREDIT source
      // row shouldn't quietly make the next entry a credit too. The IN/OUT
      // toggle is right there if this one really is a return.
      transaction_type: INCOME_ENTRY_ENABLED ? tx.transaction_type : 'DEBIT',
      ledger_category_id: String(tx.ledger_category_id || ''),
      ledger_subcategory_id: '',
      particular: '',
      amount: '',
      payment_mode: 'UPI',
      paid_from_petty_cash: false,
      transferred_from_id: '',
      sender_id: '',
      receiver_id: '',
      remarks: '',
      proof: null
    });
  };

  // Returns whether the save actually went through, so the unsaved-changes
  // prompt can keep the editor open when validation or the request fails
  // instead of discarding the user's work.
  const saveEntry = async (): Promise<boolean> => {
    // A file still uploading means draft.proof isn't set yet. Saving now would
    // drop the proof (and fail the debit-compliance check), so make the user wait
    // for the upload to finish rather than silently losing the entry.
    if (uploading) {
      toast.error('A file is still uploading — please wait for it to finish.');
      return false;
    }
    if (!isDebitCompliant(draft.transaction_type, draft.proof, draft.remarks)) {
      toast.error(COMPLIANCE_MESSAGE);
      return false;
    }

    try {
      const payload: any = {
        ...draft,
        ledger_category_id: draft.ledger_category_id ? parseInt(draft.ledger_category_id) : null,
        ledger_subcategory_id: draft.ledger_subcategory_id ? parseInt(draft.ledger_subcategory_id) : null,
        transferred_from_id: draft.transferred_from_id ? parseInt(draft.transferred_from_id) : null,
        sender_id: draft.sender_id ? parseInt(draft.sender_id) : null,
        receiver_id: draft.receiver_id ? parseInt(draft.receiver_id) : null,
        amount: amountToSave(draft.amount)
      };
      // Every slot, not just activeSlots: setDraftField clears all of 2-5 to ''
      // when a parent changes, and slots above VISIBLE_SUBLEDGER_SLOT_MAX never
      // appear in activeSlots — so a hidden slot would ride along from ...draft
      // as '' and the backend rejects the whole save with a 422.
      for (const slot of [2, 3, 4, 5]) {
        const field = `ledger_subledger_${slot}_id`;
        payload[field] = draft[field] ? parseInt(draft[field]) : null;
      }

      if (editingId === 'new') {
        await axios.post(`${API}/api/financial/transactions`, payload, { headers: getAuthHeader() });
        toast.success('Entry added');
      } else {
        await axios.patch(`${API}/api/financial/transactions/${editingId}`, payload, { headers: getAuthHeader() });
        toast.success('Entry updated');
      }

      closeEdit();
      fetchAllData();
      return true;
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to save entry'));
      return false;
    }
  };

  // Ignores keys the editor sets itself but the user can't type into, so an
  // untouched row never counts as dirty.
  const isDraftDirty = useMemo(() => {
    if (!originalDraft) return false;
    const keys = Array.from(new Set([...Object.keys(draft), ...Object.keys(originalDraft)]));
    for (const k of keys) {
      const a = draft[k], b = originalDraft[k];
      // '' , null and undefined all mean "empty" across these inputs.
      const norm = (v: any) => (v === '' || v == null ? '' : String(v));
      if (norm(a) !== norm(b)) return true;
    }
    return false;
  }, [draft, originalDraft]);

  // Escape / click-away route through here: silent when nothing changed,
  // otherwise hand off to the save-or-discard prompt.
  const requestCloseEdit = () => {
    if (editingId === null) return;
    if (isDraftDirty) setUnsavedPrompt(true);
    else closeEdit();
  };

  // Jumping straight to another row is *inside* the table, so click-away never
  // fires — without this guard the open draft would be silently overwritten.
  const guardSwitch = (open: () => void) => {
    if (editingId !== null && isDraftDirty) { setUnsavedPrompt(true); return; }
    open();
  };

  // ── Drag-to-select ────────────────────────────────────────────────────────
  // A press that never moves is a click and still opens the editor; a press
  // that travels past a few pixels becomes a range selection. Distinguishing
  // them on movement rather than on a modifier key is what lets both gestures
  // live on the same rows.
  const DRAG_THRESHOLD_PX = 4;
  const dragAnchor = useRef<number | null>(null);        // index in sortedTransactions
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const didDrag = useRef(false);

  const selectRange = (from: number, to: number) => {
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    setSelectedIds(new Set(sortedTransactions.slice(lo, hi + 1).map(t => t.id)));
  };

  const onRowMouseDown = (index: number) => (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (e.shiftKey && dragAnchor.current !== null) {
      e.preventDefault();
      didDrag.current = true;               // treat as a selection, not an edit
      selectRange(dragAnchor.current, index);
      return;
    }
    dragAnchor.current = index;
    dragOrigin.current = { x: e.clientX, y: e.clientY };
    didDrag.current = false;
  };

  const onRowMouseEnter = (index: number) => (e: React.MouseEvent) => {
    setHoveredRowId(sortedTransactions[index]?.id ?? null);
    if (dragOrigin.current === null || dragAnchor.current === null) return;
    if (e.buttons !== 1) return;            // button released outside the table
    const moved = Math.abs(e.clientX - dragOrigin.current.x) + Math.abs(e.clientY - dragOrigin.current.y);
    if (moved < DRAG_THRESHOLD_PX) return;
    didDrag.current = true;
    document.body.style.userSelect = 'none';
    selectRange(dragAnchor.current, index);
  };

  useEffect(() => {
    const onUp = () => {
      dragOrigin.current = null;
      document.body.style.userSelect = '';
      // Cleared on the next tick so the row's onClick (which fires after
      // mouseup) can still see that a drag just happened and skip the editor.
      setTimeout(() => { didDrag.current = false; }, 0);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, []);

  // Ids are meaningless once a different month is loaded, and a stale selection
  // would keep a total on screen for rows nobody can see.
  useEffect(() => { setSelectedIds(new Set()); }, [month, year]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape inside a SearchableSelect should close that dropdown first;
      // it stops propagation itself, so anything reaching here is ours.
      // Innermost thing first: prompt, then editor, then selection.
      if (unsavedPrompt) { e.preventDefault(); setUnsavedPrompt(false); return; }
      if (editingId !== null) { e.preventDefault(); requestCloseEdit(); return; }
      if (selectedIds.size > 0) { e.preventDefault(); setSelectedIds(new Set()); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    if (editingId === null || unsavedPrompt) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Inside the table itself — that's still editing, not a click away.
      if (tableWrapRef.current?.contains(target)) return;
      // Dropdown menus portal to <body>, so they're technically outside the
      // table; picking an option must not be read as a click away.
      if (target.closest('[data-portal-dropdown]')) return;
      // Toasts and any open modal overlay likewise sit outside the table.
      if (target.closest('[data-ledger-modal]')) return;
      requestCloseEdit();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  });

  const addNewCategory = async (name: string): Promise<SearchableSelectOption | null> => {
    try {
      await axios.post(`${API}/api/financial/categories`, { name }, { headers: getAuthHeader() });
      const catRes = await axios.get(`${API}/api/financial/categories`, { headers: getAuthHeader() });
      setCategories(catRes.data);
      toast.success('Category added!');
      return catRes.data.find((c: any) => c.name.toLowerCase() === name.toLowerCase()) || null;
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to add category'));
      return null;
    }
  };

  // Created under whichever ledger the row is on — never ambiguous, so there's
  // no parent picker to get wrong. Returns the created row directly rather
  // than re-finding it by name: several ledgers can now hold the same name, so
  // a name lookup would be ambiguous.
  const addNewSubcategory = async (name: string): Promise<SearchableSelectOption | null> => {
    const catId = draft.ledger_category_id;
    if (!catId) {
      toast.error('Pick a ledger first');
      return null;
    }
    try {
      const res = await axios.post(
        `${API}/api/financial/subcategories`,
        { name, ledger_category_id: Number(catId) },
        { headers: getAuthHeader() },
      );
      const subRes = await axios.get(`${API}/api/financial/subcategories`, { headers: getAuthHeader() });
      setSubcategories(subRes.data);
      // Naming the parent teaches the scoping on first use.
      toast.success(`Added under ${parentNameForSlot(1) || 'this ledger'}`);
      return { id: res.data.id, name: res.data.name };
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to add subcategory'));
      return null;
    }
  };

  // Shared add-to-list handler for the Transferred From / Sender / Receiver
  // dropdowns — one discriminated /party-options list per `kind`.
  const addNewParty = (kind: string, setOptions: (opts: any[]) => void, label: string) =>
    async (name: string): Promise<SearchableSelectOption | null> => {
      try {
        await axios.post(`${API}/api/financial/party-options`, { kind, name }, { headers: getAuthHeader() });
        const res = await axios.get(`${API}/api/financial/party-options?kind=${kind}`, { headers: getAuthHeader() });
        setOptions(res.data);
        toast.success(`${label} added!`);
        return res.data.find((o: any) => o.name.toLowerCase() === name.toLowerCase()) || null;
      } catch (err: any) {
        toast.error(apiErrorMessage(err, `Failed to add ${label.toLowerCase()}`));
        return null;
      }
    };
  // Rename a name in one of the /party-options lists. Entries that store the
  // row id (Transferred From / Sender / Receiver) follow the new spelling for
  // free. Name-keyed lists (Particulars, Remarks) store the text itself, so
  // older entries keep the old wording — renaming fixes the list, not history.
  const renameParty = (kind: string, setOptions: (opts: any[]) => void, label: string) =>
    async (opt: SearchableSelectOption, newName: string): Promise<boolean> => {
      try {
        await axios.patch(`${API}/api/financial/party-options/${opt.id}`,
          { name: newName }, { headers: getAuthHeader() });
        const res = await axios.get(`${API}/api/financial/party-options?kind=${kind}`, { headers: getAuthHeader() });
        setOptions(res.data);
        toast.success(`${label} renamed`);
        return true;
      } catch (err: any) {
        toast.error(apiErrorMessage(err, `Failed to rename ${label.toLowerCase()}`));
        return false;
      }
    };

  // Delete is attempted directly and the server decides: it refuses with a 409
  // naming where the name is still used. Asking the usage endpoint first would
  // just duplicate that check and still race with another user's entry.
  const deleteParty = (kind: string, setOptions: (opts: any[]) => void, label: string) =>
    async (opt: SearchableSelectOption) => {
      if (!window.confirm(`Delete "${opt.name}" from the ${label} list?`)) return;
      try {
        await axios.delete(`${API}/api/financial/party-options/${opt.id}`, { headers: getAuthHeader() });
        const res = await axios.get(`${API}/api/financial/party-options?kind=${kind}`, { headers: getAuthHeader() });
        setOptions(res.data);
        toast.success(`${label} deleted`);
      } catch (err: any) {
        toast.error(apiErrorMessage(err, `Failed to delete ${label.toLowerCase()}`));
      }
    };

  const addNewTransferredFrom = addNewParty('TRANSFERRED_FROM', setTransferredFromOptions, 'Transferred From');
  const addNewSender = addNewParty('SENDER', setSenderOptions, 'Sender');
  const addNewReceiver = addNewParty('RECEIVER', setReceiverOptions, 'Receiver');

  const renameTransferredFrom = renameParty('TRANSFERRED_FROM', setTransferredFromOptions, 'Transferred From');
  const deleteTransferredFrom = deleteParty('TRANSFERRED_FROM', setTransferredFromOptions, 'Transferred From');
  const renameSender   = renameParty('SENDER',   setSenderOptions,   'Sender');
  const deleteSender   = deleteParty('SENDER',   setSenderOptions,   'Sender');
  const renameReceiver = renameParty('RECEIVER', setReceiverOptions, 'Receiver');
  const deleteReceiver = deleteParty('RECEIVER', setReceiverOptions, 'Receiver');
  const renameRemarkOpt = renameParty('REMARK', setRemarkOptions, 'Remark');
  const deleteRemarkOpt = deleteParty('REMARK', setRemarkOptions, 'Remark');

  // ── Cascading subledger options ────────────────────────────────────────────
  // Each level offers only the children of the level above it:
  //   ledger -> Subledger 1 -> Subledger 2 -> 3 -> 4 -> 5
  // Filtered client-side from the lists already loaded, rather than refetching
  // per parent — the batch of dropdowns is re-derived on every keystroke in a
  // parent, and a request per change would be a waterfall for no benefit.
  //
  // `withCurrent` keeps the row being edited readable: an entry saved before
  // the cascade existed can hold a value that no longer sits under the chosen
  // parent, and SearchableSelect resolves its label from `options` — so
  // without this the box renders blank and the tag looks lost. Same trick the
  // Particulars dropdown below already uses for free-text rows.
  const withCurrent = (scoped: any[], all: any[], currentId: any) => {
    if (!currentId) return scoped;
    if (scoped.some(o => String(o.id) === String(currentId))) return scoped;
    const current = all.find(o => String(o.id) === String(currentId));
    return current ? [current, ...scoped] : scoped;
  };

  const subcategoriesForLedger = useMemo(() => {
    const catId = draft.ledger_category_id;
    const scoped = catId
      ? subcategories.filter((s: any) => String(s.ledger_category_id) === String(catId))
      : [];
    return withCurrent(scoped, subcategories, draft.ledger_subcategory_id);
  }, [subcategories, draft.ledger_category_id, draft.ledger_subcategory_id]);

  // Parent of slot 2 is the Subledger 1 row; parent of 3-5 is the value one
  // slot up, so this walks down the chain in order.
  const subledgerValuesForParent = useMemo(() => {
    const out: Record<number, any[]> = {};
    for (const slot of activeSlots) {
      const all = subledgerValues[slot] || [];
      const parentId = slot === 2
        ? draft.ledger_subcategory_id
        : draft[`ledger_subledger_${slot - 1}_id`];
      const scoped = parentId
        ? all.filter((v: any) => String(
            slot === 2 ? v.parent_subcategory_id : v.parent_value_id
          ) === String(parentId))
        : [];
      out[slot] = withCurrent(scoped, all, draft[`ledger_subledger_${slot}_id`]);
    }
    return out;
  }, [subledgerValues, activeSlots, draft]);

  // Name of the parent a new value would be created under, for the "Add X to
  // <parent>" footer and the "pick a parent first" placeholder.
  const parentNameForSlot = (slot: number): string => {
    if (slot === 1) {
      return categories.find((c: any) => String(c.id) === String(draft.ledger_category_id))?.name || '';
    }
    const parentId = slot === 2
      ? draft.ledger_subcategory_id
      : draft[`ledger_subledger_${slot - 1}_id`];
    if (!parentId) return '';
    if (slot === 2) {
      return subcategories.find((s: any) => String(s.id) === String(parentId))?.name || '';
    }
    return (subledgerValues[slot - 1] || [])
      .find((v: any) => String(v.id) === String(parentId))?.name || '';
  };

  // Changing a level invalidates everything below it — those values belong to
  // the parent that was just replaced. Clearing is the honest behaviour (an
  // out-of-scope tag is exactly the data-quality problem this feature exists
  // to prevent); the toast is what stops it reading as a bug.
  //
  // Only fires on an actual change to an already-set field, so filling in a
  // blank row clears nothing. Opening an existing entry for edit seeds the
  // draft through beginEdit, never here, so historical tags are never touched.
  const setDraftField = (field: string, val: any) => {
    const LEVELS = ['ledger_category_id', 'ledger_subcategory_id',
      ...[2, 3, 4, 5].map(s => `ledger_subledger_${s}_id`)];
    const level = LEVELS.indexOf(field);
    const prev = draft[field];
    if (level === -1 || !prev || String(prev) === String(val)) {
      setDraft({ ...draft, [field]: val });
      return;
    }
    const below = LEVELS.slice(level + 1);
    const hadTags = below.some(f => draft[f]);
    setDraft({
      ...draft,
      [field]: val,
      ...Object.fromEntries(below.map(f => [f, ''])),
    });
    if (hadTags) toast('Subledgers below cleared — they belonged to the previous selection');
  };

  // Particulars is keyed by name, not id — the transaction column is text, so
  // the option's own name is what gets stored. SearchableSelect supports this
  // (its `id` is typed `number | string` for exactly these name-keyed lists).
  // Same name-keyed shape the hidden Particulars picker used, and for the same
  // reason:
  // the column is text, so the option's own name is what gets stored, and the
  // row's current value is surfaced as an option or it would look blank.
  const remarkSelectOptions = useMemo(() => {
    const opts = remarkOptions.map((o: any) => ({ id: o.name, name: o.name }));
    const current = draft.remarks;
    if (current && !opts.some(o => o.name.toLowerCase() === String(current).toLowerCase())) {
      opts.unshift({ id: String(current), name: String(current) });
    }
    return opts;
  }, [remarkOptions, draft.remarks]);

  const addNewRemark = async (name: string): Promise<SearchableSelectOption | null> => {
    try {
      await axios.post(`${API}/api/financial/party-options`, { kind: 'REMARK', name }, { headers: getAuthHeader() });
      const res = await axios.get(`${API}/api/financial/party-options?kind=REMARK`, { headers: getAuthHeader() });
      setRemarkOptions(res.data);
      toast.success('Remark added!');
      return { id: name, name };
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to add remark'));
      return null;
    }
  };

  // Password-gated on the server, so this opens the confirm modal rather than
  // deleting straight away.
  const deleteEntry = (id: number) => {
    setDeleteEntryTarget(transactions.find((t: any) => t.id === id) || { id });
    setDeleteEntryPassword('');
  };

  const confirmDeleteEntry = async () => {
    if (!deleteEntryTarget) return;
    if (!deleteEntryPassword) { toast.error('Enter your password to confirm'); return; }
    setDeletingEntry(true);
    try {
      await axios.delete(`${API}/api/financial/transactions/${deleteEntryTarget.id}`, {
        headers: getAuthHeader(),
        data: { password: deleteEntryPassword },
      });
      toast.success('Entry deleted');
      setDeleteEntryTarget(null);
      setDeleteEntryPassword('');
      fetchAllData();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to delete entry'));
    } finally {
      setDeletingEntry(false);
    }
  };

  // ── Rename handlers (inline typo fixes from the dropdowns) ──
  // Each PATCHes the name, then refreshes the affected list plus the
  // transactions (their displayed names change too). Returns true on success
  // so SearchableSelect can leave inline-edit mode.
  const renameCategory = async (opt: SearchableSelectOption, newName: string): Promise<boolean> => {
    try {
      await axios.patch(`${API}/api/financial/categories/${opt.id}`, { name: newName }, { headers: getAuthHeader() });
      // The filter stores names, not ids, so a rename has to be carried across
      // or the ledger silently drops out of the filtered view.
      setLedgerFilter((columnFilters['ledgerName'] || []).map(n => (n === opt.name ? newName : n)));
      toast.success('Ledger name updated');
      await fetchAllData();
      return true;
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to rename'));
      return false;
    }
  };

  const renameSubcategory = async (opt: SearchableSelectOption, newName: string): Promise<boolean> => {
    try {
      await axios.patch(`${API}/api/financial/subcategories/${opt.id}`, { name: newName }, { headers: getAuthHeader() });
      toast.success('Subledger 1 value updated');
      await fetchAllData();
      return true;
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to rename'));
      return false;
    }
  };

  const renameSubledgerValue = (slot: number) => async (opt: SearchableSelectOption, newName: string): Promise<boolean> => {
    try {
      await axios.patch(`${API}/api/financial/subledger-values/${opt.id}`, { name: newName }, { headers: getAuthHeader() });
      toast.success(`Subledger ${slot} value updated`);
      await Promise.all([fetchSubledgerValues(slot), fetchAllData()]);
      return true;
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to rename'));
      return false;
    }
  };

  // ── Delete flow: open a confirm modal showing the affected entries ──
  const openDelete = async (target: DeleteTarget) => {
    setDeleteTarget(target);
    setDeleteUsage(null);
    setDeletePassword('');
    setDeleteUsageLoading(true);
    try {
      const res = await axios.get(`${API}/api/financial/${usageBasePath(target)}/usage`, { headers: getAuthHeader() });
      setDeleteUsage(res.data);
    } catch {
      setDeleteUsage({ count: 0, sample: [] });
    } finally {
      setDeleteUsageLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { kind, slot, option } = deleteTarget;
    if (kind === 'category' && !deletePassword) { toast.error('Enter your password to confirm'); return; }
    setDeleting(true);
    try {
      if (kind === 'category') {
        await axios.delete(`${API}/api/financial/categories/${option.id}`, { headers: getAuthHeader(), data: { password: deletePassword } });
        setLedgerFilter((columnFilters['ledgerName'] || []).filter(n => n !== option.name));
      } else if (kind === 'subcategory') {
        await axios.delete(`${API}/api/financial/subcategories/${option.id}`, { headers: getAuthHeader() });
      } else {
        await axios.delete(`${API}/api/financial/subledger-values/${option.id}`, { headers: getAuthHeader() });
      }
      toast.success('Deleted');
      setDeleteTarget(null);
      await fetchAllData();
      if (kind === 'subledger' && slot) await fetchSubledgerValues(slot);
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to delete'));
    } finally {
      setDeleting(false);
    }
  };

  // Every column that gets a filter dropdown, and which transaction field it
  // reads. Ledger is in here like the rest so all seven behave identically;
  // the ?category= deep link from the P&L seeds it on mount instead of living
  // in its own separate piece of state.
  const filterableColumns = useMemo(() => [
    { id: 'ledgerName', label: 'Ledger',      field: 'ledger_category_name' },
    { id: 'subledger1', label: 'Subledger 1', field: 'ledger_subcategory_name' },
    ...activeSlots.map(slot => ({
      id: `subledger-${slot}`, label: `Subledger ${slot}`, field: `ledger_subledger_${slot}_name`,
    })),
    { id: 'mode',       label: 'Mode',        field: 'payment_mode' },
  ], [activeSlots]);

  const ledgerFilter = columnFilters['ledgerName'] || [];

  const columnFieldById = useMemo(
    () => Object.fromEntries(filterableColumns.map(c => [c.id, c.field])),
    [filterableColumns]
  );

  // Options come from the whole month, not the filtered set, so narrowing one
  // dropdown never makes another dropdown's choices disappear.
  const columnFilterOptions = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const col of filterableColumns) {
      // Ledgers list in the client's chosen order (from the Reorder modal)
      // rather than being derived from the month's data.
      if (col.id === 'ledgerName') {
        out[col.id] = categories.map((c: any) => c.name);
        continue;
      }
      const seen = new Set<string>();
      let hasBlank = false;
      for (const t of transactions) {
        const v = t[col.field];
        if (v == null || v === '') hasBlank = true;
        else seen.add(String(v));
      }
      out[col.id] = [...Array.from(seen).sort((a, b) => a.localeCompare(b)), ...(hasBlank ? [BLANK_VALUE] : [])];
    }
    return out;
  }, [filterableColumns, transactions]);

  // Every column the search box looks at, lower-cased, in one place so the
  // filter and the exact-match ranking below can never drift apart.
  //
  // Numbers are stringified deliberately: SL No. and Amount are the two things
  // people reach for most, and a `typeof f === 'string'` test used to drop them
  // silently. Amount is offered both raw (1500.00) and grouped (1,500.00) so
  // either way of typing it hits.
  const searchFieldsOf = (t: any): string[] => {
    const amount = parseFloat(t.amount);
    return [
      t.ledger_no,
      t.particular, t.remarks,
      t.ledger_category_name, t.ledger_subcategory_name,
      t.transferred_from_name, t.sender_name, t.receiver_name, t.entered_by_email,
      t.payment_mode, String(t.payment_mode || '').replace(/_/g, ' '),
      t.transaction_date,
      t.transaction_date
        ? new Date(t.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '',
      ...activeSlots.map(slot => t[`ledger_subledger_${slot}_name`]),
      ...(Number.isFinite(amount)
        ? [String(amount), amount.toFixed(2), amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })]
        : []),
    ]
      .filter(f => f != null && f !== '')
      .map(f => String(f).toLowerCase());
  };

  // Drives the badge on the Filters button, so it has to count the Ledger
  // dropdown too even though that one lives in its own state. Search isn't
  // counted — that box is always visible, so it speaks for itself.
  const activeFilterCount = Object.entries(columnFilters).filter(([colId, picked]) => {
    if (!picked || picked.length === 0) return false;
    // Both petty-cash boxes ticked shows everything, so nothing is being
    // narrowed — the badge would be claiming a filter that isn't one.
    if (colId === PETTY_FILTER_ID && picked.length > 1) return false;
    return true;
  }).length;

  const filteredTransactions = transactions.filter(t => {
    // Exactly one box ticked is the only case that filters; neither and both
    // alike mean "show everything".
    if (pettyPicked.length === 1 && !!t.paid_from_petty_cash !== (pettyPicked[0] === 'PETTY')) {
      return false;
    }
    // Expenses are DEBIT, returns CREDIT; no pick at all means show both.
    if (kindPicked && t.transaction_type !== (kindPicked === 'RETURN' ? 'CREDIT' : 'DEBIT')) {
      return false;
    }
    // Day range within the chosen month. Either end can stand alone, so
    // "from the 15th" and "up to the 15th" both work.
    if (dateFrom || dateTo) {
      const day = dayOf(t.transaction_date);
      if (!day) return false;
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
    }
    for (const [colId, picked] of Object.entries(columnFilters)) {
      if (colId === PETTY_FILTER_ID) continue;   // handled above; not a column value
      if (colId === DATE_FILTER_ID) continue;    // handled above; a range, not a value list
      if (!picked || picked.length === 0) continue;
      const field = columnFieldById[colId];
      if (!field) continue;   // column no longer exists (a Subledger slot was removed)
      const raw = t[field];
      const value = raw == null || raw === '' ? BLANK_VALUE : String(raw);
      if (!picked.includes(value)) return false;
    }
    if (searchText.trim()) {
      if (!searchFieldsOf(t).some(f => f.includes(searchText.trim().toLowerCase()))) return false;
    }
    return true;
  });

  // In 'ledger' mode rows read as Ledger → Subledger → Date groups: every entry
  // under one ledger sits together, subdivided by Subledger 1 (then 2, 3, 4…),
  // with the sort dropdown ordering rows *within* the innermost group. In
  // 'date' mode the group keys are skipped and the dropdown alone decides the
  // order — the plain chronological read the backend already returns.
  //
  // Names are compared A→Z, with blanks pushed last so untagged "-" rows don't
  // lead a group.
  const groupKeysOf = (t: any): string[] => [
    t.ledger_category_name || '',
    t.ledger_subcategory_name || '',
    ...activeSlots.map(slot => t[`ledger_subledger_${slot}_name`] || ''),
  ];

  const compareName = (a: string, b: string) => {
    if (a === b) return 0;
    if (!a) return 1;   // blanks last
    if (!b) return -1;
    return a.localeCompare(b);
  };

  // The ledger level follows the client's chosen order (set in the Reorder
  // modal), not the alphabet. Subledger levels stay alphabetical — those lists
  // hold vehicle numbers and client names, where A→Z is what you want.
  // A name with no rank (deleted category still on an old row) sorts last.
  const categoryRank = useMemo(() => {
    const map: Record<string, number> = {};
    categories.forEach((c: any, i: number) => { map[c.name] = c.display_order ?? i + 1; });
    return map;
  }, [categories]);

  const compareLedger = (a: string, b: string) => {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    const ra = categoryRank[a] ?? Number.MAX_SAFE_INTEGER;
    const rb = categoryRank[b] ?? Number.MAX_SAFE_INTEGER;
    return ra - rb || a.localeCompare(b);
  };

  // The chosen ordering, plus a stable id tie-break so equal rows never shuffle.
  const compareWithin = (a: any, b: any) => {
    // Entries with no SL No. yet sort last rather than leading as 0, so a blank
    // can't sit above the numbered rows.
    if (sortOrder === 'sl-asc') {
      const na = a.ledger_no ?? null, nb = b.ledger_no ?? null;
      if (na === null && nb === null) return b.id - a.id;
      if (na === null) return 1;
      if (nb === null) return -1;
      return na - nb || b.id - a.id;
    }
    if (sortOrder === 'date-asc')    return new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime() || b.id - a.id;
    if (sortOrder === 'amount-desc') return parseFloat(b.amount) - parseFloat(a.amount) || b.id - a.id;
    if (sortOrder === 'amount-asc')  return parseFloat(a.amount) - parseFloat(b.amount) || b.id - a.id;
    return new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime() || b.id - a.id;
  };

  // While searching, a row whose field equals the term outright ranks above one
  // that merely contains it — type an SL No. or an exact amount and that entry
  // leads, instead of being buried among the rows that happen to contain the
  // digits. 0 = exact, 1 = substring only.
  const searchRank = (t: any): number => {
    const q = searchText.trim().toLowerCase();
    if (!q) return 0;
    return searchFieldsOf(t).some(f => f === q) ? 0 : 1;
  };

  const searching = !!searchText.trim();
  const sortingBySl = sortOrder === 'sl-asc';
  // Both orderings cut across the ledger groups, so the grouping and every
  // visual cue that goes with it (group heads, the boundary rules) steps aside.
  const ungrouped = searching || sortingBySl;

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    // Exact hits lead, and ledger grouping is set aside for the duration: a
    // search is a "find this row" question, and hoisting a row out of its group
    // would leave the group borders drawing boundaries that no longer exist.
    if (searching) {
      const byExact = searchRank(a) - searchRank(b);
      if (byExact !== 0) return byExact;
      return compareWithin(a, b);
    }
    // Sorting by SL No. sets grouping aside the same way a search does: asking
    // for serial order means one unbroken run of numbers, and keeping the
    // ledger groups would only order serials inside each group.
    if (groupBy === 'ledger' && !sortingBySl) {
      const ka = groupKeysOf(a), kb = groupKeysOf(b);
      for (let i = 0; i < ka.length; i++) {
        // Index 0 is the ledger — client order. Everything below it is A→Z.
        const cmp = i === 0 ? compareLedger(ka[0], kb[0]) : compareName(ka[i], kb[i]);
        if (cmp !== 0) return cmp;
      }
    }
    return compareWithin(a, b);
  });

  const hasActiveFilter = activeFilterCount > 0 || !!searchText.trim();

  // Net of exactly what's on screen — credits add, debits subtract, the same
  // sign convention the rows use. Driven off filteredTransactions so it tracks
  // the month, every filter and the search box without extra bookkeeping.
  const signedAmount = (t: any) =>
    (t.transaction_type === 'CREDIT' ? 1 : -1) * (parseFloat(t.amount) || 0);

  const visibleNetTotal = filteredTransactions.reduce((sum, t) => sum + signedAmount(t), 0);

  const selectedTotal = sortedTransactions
    .filter(t => selectedIds.has(t.id))
    .reduce((sum, t) => sum + signedAmount(t), 0);

  // ── Export of exactly what's on screen ──────────────────────────────────
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  const periodLabel = `${new Date(year, month - 1).toLocaleString('default', { month: 'long' })} ${year}`;

  const dateRangeLabel =
    !dateFrom && !dateTo ? 'All dates'
    : dateFrom && dateTo && dateFrom === dateTo ? prettyDay(dateFrom)
    : dateFrom && dateTo ? `${prettyDay(dateFrom)} – ${prettyDay(dateTo)}`
    : dateFrom ? `From ${prettyDay(dateFrom)}`
    : `Up to ${prettyDay(dateTo)}`;

  const pickedNames = (colId: string) => {
    const picked = columnFilters[colId] || [];
    if (picked.length === 0) return '';
    // " + " rather than a comma, so two picks at one level can't be misread as
    // a step down to the next one.
    return picked.map(v => (v === BLANK_VALUE ? '(blanks)' : v)).join(' + ');
  };

  // The ledger hierarchy written the way the client says it out loud —
  // "Transport / Fuel / Four Wheeler" — instead of a labelled line per level.
  const ledgerPath = (() => {
    // Mode sits alongside the hierarchy rather than inside it, so it stays out.
    const levels = filterableColumns
      .filter(col => col.id !== 'mode')
      .map(col => pickedNames(col.id));
    // Trailing unfiltered levels are just noise. An unfiltered level with
    // something filtered *below* it keeps its place, so the depth still reads
    // correctly — "Transport / All / Four Wheeler" is the honest rendering.
    while (levels.length && !levels[levels.length - 1]) levels.pop();
    if (levels.length === 0) return 'All ledgers';
    return levels.map(l => l || 'All').join(' / ');
  })();

  // The file name says both that this is the filtered view — not the toolbar's
  // whole-month ⭳ Download — and the exact span it covers, so a folder full of
  // these stays tellable apart without opening them.
  const exportFileBase =
    dateFrom && dateTo && dateFrom === dateTo ? `Filtered-Ledger_${prettyDay(dateFrom)}`
    : dateFrom && dateTo ? `Filtered-Ledger_${prettyDay(dateFrom)} to ${prettyDay(dateTo)}`
    : dateFrom ? `Filtered-Ledger_from ${prettyDay(dateFrom)}`
    : dateTo ? `Filtered-Ledger_upto ${prettyDay(dateTo)}`
    : `Filtered-Ledger_${periodLabel}`;

  // The filters that sit outside the hierarchy. Each gets a line whether or not
  // it's narrowing anything — a reader holding the print-out can't otherwise
  // tell a filter set to All from one that was never offered.
  const exportSummary = (): [string, string][] => [
    // No 'Month' line — the period is already the heading's subtitle.
    ['Date', dateRangeLabel],
    ['Mode', pickedNames('mode') || 'All'],
    ['Paid From', pettyPicked.length === 1
      ? (pettyPicked[0] === 'PETTY' ? 'Petty cash' : 'Non-petty cash')
      : 'Petty cash + Non-petty cash'],
    ['Entries', kindPicked === 'RETURN' ? 'Returns only'
      : kindPicked === 'EXPENSE' ? 'Expenses only'
      : 'Expenses + Returns'],
    ...(searchText.trim() ? ([['Search', searchText.trim()]] as [string, string][]) : []),
  ];

  const exportFiltered = async (kind: 'pdf' | 'excel') => {
    if (sortedTransactions.length === 0) return;
    setExporting(kind);
    try {
      const opts = {
        rows: sortedTransactions,
        activeSlots,
        ledgerPath,
        summary: exportSummary(),
        periodLabel,
        fileBase: exportFileBase,
        netTotal: visibleNetTotal,
      };
      if (kind === 'excel') await downloadLedgerExcel(opts);
      else downloadLedgerPdf(opts);
      toast.success(`Downloaded ${sortedTransactions.length} entr${sortedTransactions.length === 1 ? 'y' : 'ies'}`);
    } catch (err) {
      console.error('ledger export failed', err);
      toast.error('Could not build the download');
    } finally {
      setExporting(null);
    }
  };

  // Slots run 2..VISIBLE_SUBLEDGER_SLOT_MAX, so that's how many there can be.
  // The "+" cell shows only while one is still unrevealed — it must track the
  // visible cap, or it would offer a slot the workspace then filters out.
  const canAddSubledger = activeSlots.length < VISIBLE_SUBLEDGER_SLOT_MAX - 1;

  // 12 fixed columns (incl. SL No. and Transferred From / Sender / Receiver) +
  // one per active Subledger slot + the "+" header cell (only while there's
  // still room to add more), kept in sync with the <thead> below so colSpan on
  // the loading/empty rows always matches. Was 13 before Particulars was
  // hidden — see HIDDEN_PARTICULARS below.
  const tableColumnCount = 12 + activeSlots.length + (canAddSubledger ? 1 : 0);

  // Every column with its default width, 'actions' included — each one has its
  // own drag handle and resizes independently of its neighbours. This is the
  // single source of truth for the widths: the <thead> below reads its defaults
  // from here, and the table's own width is the sum of them (see useTableWidth).
  const columns = useMemo(() => [
    { id: 'slNo', defaultWidth: 55 },
    { id: 'date', defaultWidth: 85 },
    { id: 'ledgerName', defaultWidth: 110 },
    { id: 'subledger1', defaultWidth: 100 },
    ...activeSlots.map(slot => ({ id: `subledger-${slot}`, defaultWidth: 100 })),
    { id: 'mode', defaultWidth: 70 },
    { id: 'amount', defaultWidth: 100 },
    { id: 'transferredFrom', defaultWidth: 120 },
    { id: 'sender', defaultWidth: 120 },
    { id: 'receiver', defaultWidth: 120 },
    { id: 'proof', defaultWidth: 90 },
    { id: 'remarks', defaultWidth: 110 },
    { id: 'actions', defaultWidth: 100 },
  ], [activeSlots]);
  const columnOrder = useMemo(() => columns.map(c => c.id), [columns]);
  const defaultWidthOf = (id: string) => columns.find(c => c.id === id)!.defaultWidth;



  return (
    <div style={s.page}>

      {/* ── HEADER BAR ── */}
      <div style={s.headerBar}>
        <div>
          <h1 style={s.title}>
            Ledger Workspace
            {ledgerFilter.length === 1 && <span style={s.filterBadge}>{ledgerFilter[0]}</span>}
            {ledgerFilter.length > 1 && <span style={s.filterBadge}>{ledgerFilter.length} ledgers</span>}
          </h1>
          <p style={s.subtitle}>
            {sortedTransactions.length} {sortedTransactions.length === 1 ? 'entry' : 'entries'}
            {hasActiveFilter ? ' (filtered)' : ''}
          </p>
        </div>
        <button onClick={() => navigate('/financial')} style={s.btnGhost}>← Back</button>
      </div>

      {/* ── TOOLBAR ── */}
      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <select style={s.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({length: 12}, (_, i) => (
              <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <select style={s.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {[year-1, year, year+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <input
            type="text"
            style={s.searchInput}
            placeholder="Search particulars, remarks, names…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          {/* Seven dropdowns that read "All" most of the time were carrying more
              visual weight than the table itself, so they live in a panel that
              only opens when wanted. The badge is what tells you filtering is on
              while the panel is shut. */}
          <button
            onClick={() => setFiltersOpen(o => !o)}
            style={{
              ...s.btnGhost,
              display: 'inline-flex', alignItems: 'center', gap: spacing(2),
              ...(activeFilterCount > 0
                ? { color: colors.primaryDark, borderColor: colors.primary, background: colors.primaryBg }
                : {}),
            }}
            title="Filter by ledger, subledger or payment mode"
          >
            <span style={{ display: 'inline-block', width: 8, transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform .12s' }}>⌄</span>
            Filters
            {activeFilterCount > 0 && (
              <span style={{
                background: colors.primary, color: '#fff', borderRadius: radius.pill,
                fontSize: '0.7rem', fontWeight: fontWeight.bold, padding: '0 6px', lineHeight: '16px',
              }}>{activeFilterCount}</span>
            )}
          </button>
          {/* Sits beside Filters rather than inside the panel, so filters can be
              cleared without opening it — and so it's visible at all when the
              panel is shut but something is still filtering the table. */}
          {hasActiveFilter && (
            <button
              onClick={() => { setColumnFilters({}); setSearchText(''); }}
              style={{ ...s.btnGhost, color: colors.danger, borderColor: colors.border }}
              title="Clear every filter and the search box"
            >
              Clear filters ✕
            </button>
          )}
        </div>
        <div style={s.toolbarRight}>
          {/* Net of exactly what's on screen, so it moves with every filter and
              the search box — not a month total. Drag-selecting rows narrows it
              further to just those. */}
          {(() => {
            const selecting = selectedIds.size > 0;
            const total = selecting ? selectedTotal : visibleNetTotal;
            return (
              <div style={s.subtotalBox}>
                <span style={s.subtotalLabel}>
                  {selecting ? `${selectedIds.size} selected` : 'Subtotal'}
                  {selecting && (
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      title="Clear selection (Esc)"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: '0 0 0 4px', fontSize: '0.8rem', lineHeight: 1 }}
                    >✕</button>
                  )}
                </span>
                <span style={{ ...s.subtotalValue, color: total < 0 ? colors.danger : colors.success }}>
                  {total < 0 ? '-' : '+'} ₹{Math.abs(total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            );
          })()}
          <select style={s.select} value={groupBy} onChange={e => setGroupBy(e.target.value as typeof groupBy)} title="Ledger groups every entry under one ledger together; Date is a plain chronological list">
            <option value="ledger">Group by: Ledger</option>
            <option value="date">Group by: Date</option>
          </select>
          {/* In Ledger mode this only orders rows inside a group, so the label
              says so — otherwise "Date: Newest" reads like it sorts the table. */}
          <select style={s.select} value={sortOrder} onChange={e => setSortOrder(e.target.value as typeof sortOrder)}>
            <option value="date-desc">{groupBy === 'ledger' ? 'In group: Newest' : 'Date: Newest'}</option>
            <option value="date-asc">{groupBy === 'ledger' ? 'In group: Oldest' : 'Date: Oldest'}</option>
            <option value="amount-desc">{groupBy === 'ledger' ? 'In group: Highest' : 'Amount: Highest'}</option>
            <option value="amount-asc">{groupBy === 'ledger' ? 'In group: Lowest' : 'Amount: Lowest'}</option>
            {/* Listed here too, so ticking SL No. in the filter panel doesn't
                leave this dropdown showing a value it isn't using. SL No. sorts
                the whole table, so it never takes the "In group:" wording. */}
            <option value="sl-asc">SL No.</option>
          </select>
          {/* Only affects the Ledger grouping, so it's hidden in Date mode
              where ledger order has no bearing on what you see. */}
          {groupBy === 'ledger' && (
            <button onClick={() => setReorderOpen(true)} style={s.btnGhost} title="Choose the order ledgers are grouped in">
              ⇅ Reorder
            </button>
          )}
          <button onClick={() => setDownloadOpen(true)} style={s.btnGhost} title="Download the ledger as an Excel file">
            ⭳ Download
          </button>
          <div style={s.btnGroup}>
            {/* Income entry hidden — client wants income to come only from
                billing, not manual ledger entries. Kept behind a flag rather
                than removed, since the client may want it back. */}
            {INCOME_ENTRY_ENABLED && (
              <button onClick={() => startNewEntry('CREDIT')} disabled={editingId !== null} style={{...s.btnIncome, opacity: editingId ? 0.5 : 1}}>
                + Add Income
              </button>
            )}
            {/* Money coming back — a refund or reimbursement. Same CREDIT
                mechanics as income, but scoped to this screen and its own flag
                so it can exist while income entry stays hidden. Tick "Petty
                cash" on the row for it to land back in the drawer. */}
            {RETURN_ENTRY_ENABLED && (
              <button onClick={() => startNewEntry('CREDIT')} disabled={editingId !== null} style={{...s.btnIncome, opacity: editingId ? 0.5 : 1}} title="Record money coming back — a refund or reimbursement">
                + Add Return
              </button>
            )}
            <button onClick={() => startNewEntry('DEBIT')} disabled={editingId !== null} style={{...s.btnExpense, opacity: editingId ? 0.5 : 1}}>
              + Add Expense
            </button>
          </div>
        </div>
      </div>

      {/* ── FILTER PANEL ── */}
      {filtersOpen && (
        <div style={s.filterPanel}>
          <div style={s.filterGrid}>
            {filterableColumns.map(col => (
              <div key={col.id} style={s.filterField}>
                <span style={s.filterLabel}>{col.label}</span>
                <ColumnFilter
                  label={col.label}
                  options={columnFilterOptions[col.id] || []}
                  selected={columnFilters[col.id] || []}
                  onChange={next => setColumnFilters(prev => ({ ...prev, [col.id]: next }))}
                />
              </div>
            ))}
            {/* Tick boxes rather than another dropdown: it's a two-value
                choice, and leaving both clear (the default) is the "show me
                everything" state the client expects to land on. */}
            <div style={s.filterField}>
              <span style={s.filterLabel}>Paid From</span>
              <div style={s.pettyBoxes}>
                {([['PETTY', 'Petty cash', colors.textSecondary],
                   ['NON_PETTY', 'Non-petty cash', NON_PETTY_COLOR]] as const).map(([choice, label, color]) => (
                  <label key={choice} style={{ ...s.pettyBox, color }}>
                    <input
                      type="checkbox"
                      checked={pettyPicked.includes(choice)}
                      onChange={() => togglePetty(choice)}
                      style={{ cursor: 'pointer', accentColor: colors.primary, flexShrink: 0 }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Radios, not tick boxes: the three are mutually exclusive, and
                "All" is a real choice the client can click back to rather than
                a state you reach by unticking things. */}
            <div style={s.filterField}>
              <span style={s.filterLabel}>Entries</span>
              <div style={s.pettyBoxes}>
                {([[null, 'All', colors.textSecondary],
                   ['EXPENSE', 'Expenses only', colors.textSecondary],
                   ['RETURN', 'Returns only', colors.success]] as const).map(([choice, label, color]) => (
                  <label key={label} style={{ ...s.pettyBox, color }}>
                    <input
                      type="radio"
                      name="ledger-entry-kind"
                      checked={(kindPicked ?? null) === choice}
                      onChange={() => setKind(choice)}
                      style={{ cursor: 'pointer', accentColor: colors.primary, flexShrink: 0 }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Sorting rather than filtering, but it lives here because it's
                the same "narrow the view" gesture. Ticking it takes over the
                sort (and suspends ledger grouping, so the serials read as one
                unbroken run); clearing it hands the sort back to whatever the
                toolbar dropdown says. */}
            <div style={s.filterField}>
              <span style={s.filterLabel}>Sort</span>
              <div style={s.pettyBoxes}>
                <label style={{ ...s.pettyBox, color: colors.textSecondary }}>
                  <input
                    type="checkbox"
                    checked={sortingBySl}
                    onChange={() => setSortOrder(sortingBySl ? 'date-asc' : 'sl-asc')}
                    style={{ cursor: 'pointer', accentColor: colors.primary, flexShrink: 0 }}
                  />
                  By SL No.
                </label>
              </div>
            </div>

            {/* Scoped to the month already chosen in the toolbar, so the
                calendar can't wander outside the data the page has loaded.
                Click one day, or drag across days for a span. */}
            <div style={s.filterField}>
              <span style={s.filterLabel}>Date</span>
              <DayRangeCalendar
                year={year}
                month={month}
                from={dateFrom}
                to={dateTo}
                onChange={setDateRange}
              />
            </div>

            {/* Exports exactly the rows left on screen. Sits in the filter panel
                rather than the toolbar precisely because it's downstream of the
                filters — the toolbar's ⭳ Download is the other thing, a
                server-side pull with its own independent filter UI. */}
            <div style={s.filterField}>
              <span style={s.filterLabel}>Download filtered</span>
              <div style={s.downloadBox}>
                <button
                  onClick={() => exportFiltered('pdf')}
                  disabled={exporting !== null || sortedTransactions.length === 0}
                  style={{ ...s.downloadBtn, ...(sortedTransactions.length === 0 ? s.downloadBtnOff : {}) }}
                  title="Download what's on screen as a PDF"
                >
                  {exporting === 'pdf' ? '…' : 'PDF'}
                </button>
                <span style={s.downloadDivider} />
                <button
                  onClick={() => exportFiltered('excel')}
                  disabled={exporting !== null || sortedTransactions.length === 0}
                  style={{ ...s.downloadBtn, ...(sortedTransactions.length === 0 ? s.downloadBtnOff : {}) }}
                  title="Download what's on screen as an Excel sheet"
                >
                  {exporting === 'excel' ? '…' : 'Excel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SPREADSHEET TABLE ── */}
      <div ref={tableWrapRef} style={s.tableWrap}>
          {/* v6: a clean break. Widths saved before this point were set against
              a table that quietly padded them out, so honouring them literally
              (as the resizer now does) renders them far narrower than their
              owners ever intended — and the old 'actions' width is the very
              bloat this set out to fix. Everyone starts from the defaults in
              `columns` and resizes from there. */}
          <ResizableColumnsProvider
            storageKey="ledger-workspace-v7"
            columnOrder={columnOrder}
          >
          <SizedTable columns={columns} extraWidth={canAddSubledger ? PLUS_COL_WIDTH : 0}>
            <thead>
              <tr>
                <ResizableTh columnId="slNo" defaultWidth={defaultWidthOf('slNo')} style={s.th}>SL No.</ResizableTh>
                <ResizableTh columnId="date" defaultWidth={defaultWidthOf('date')} style={s.th}>Date</ResizableTh>
                <ResizableTh columnId="ledgerName" defaultWidth={defaultWidthOf('ledgerName')} style={s.th}>Ledger Name</ResizableTh>
                <ResizableTh columnId="subledger1" defaultWidth={defaultWidthOf('subledger1')} style={s.th}>Subledger 1</ResizableTh>
                {activeSlots.map(slot => (
                  <ResizableTh key={slot} columnId={`subledger-${slot}`} defaultWidth={defaultWidthOf(`subledger-${slot}`)} style={s.th}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing(1) }}>
                      Subledger {slot}
                      <button
                        onClick={() => removeSubledgerColumn(slot)}
                        title={`Remove Subledger ${slot} (only if it has no entries)`}
                        style={{ background: '#ef4444', border: 'none', cursor: 'pointer', color: 'white', fontSize: fontSize.sm, fontWeight: fontWeight.bold, lineHeight: 1, width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
                      >×</button>
                    </span>
                  </ResizableTh>
                ))}
                {canAddSubledger && (
                  <th style={{...s.th, width: `${PLUS_COL_WIDTH}px`}}>
                    <button onClick={addSubledgerColumn} title="Add another Subledger column" style={s.addRowBtn}>+</button>
                  </th>
                )}
                <ResizableTh columnId="mode" defaultWidth={defaultWidthOf('mode')} style={s.th}>Mode</ResizableTh>
                <ResizableTh columnId="amount" defaultWidth={defaultWidthOf('amount')} style={s.th}>Amount (₹)</ResizableTh>
                <ResizableTh columnId="transferredFrom" defaultWidth={defaultWidthOf('transferredFrom')} style={s.th}>Transferred From</ResizableTh>
                <ResizableTh columnId="sender" defaultWidth={defaultWidthOf('sender')} style={s.th}>Sender</ResizableTh>
                <ResizableTh columnId="receiver" defaultWidth={defaultWidthOf('receiver')} style={s.th}>Receiver</ResizableTh>
                <ResizableTh columnId="proof" defaultWidth={defaultWidthOf('proof')} style={s.th}>Proof</ResizableTh>
                <ResizableTh columnId="remarks" defaultWidth={defaultWidthOf('remarks')} style={s.th}>Remarks</ResizableTh>
                <ResizableTh columnId="actions" defaultWidth={defaultWidthOf('actions')} style={s.th}>Actions</ResizableTh>
              </tr>
            </thead>
            <tbody>

              {/* ── INLINE EDIT ROW ── */}
              {editingId && (() => {
                const needsRemark = !isDebitCompliant(draft.transaction_type, draft.proof, draft.remarks);
                return (
                  <tr style={{ background: '#fef9c3' }}>
                    <td style={{...s.td, color: colors.textFaint}}>—</td>
                    <td style={s.td}>
                      <input type="date" style={s.input} value={draft.transaction_date || ''} onChange={e => setDraft({...draft, transaction_date: e.target.value})} />
                      <div style={{ display: 'flex', gap: 4, marginTop: 4, justifyContent: 'center' }}>
                        {/* IN hidden for brand-new rows only when neither
                            income nor returns are on — with returns enabled a
                            new row can legitimately be a CREDIT, and the
                            toggle is how you fix picking the wrong button.
                            Editing an existing CREDIT row always shows both,
                            since that's correcting data, not creating income. */}
                        {(INCOME_ENTRY_ENABLED || RETURN_ENTRY_ENABLED || editingId !== 'new') && (
                          <button onClick={() => setDraft({...draft, transaction_type: 'CREDIT'})} style={{...s.typeToggle, ...(draft.transaction_type === 'CREDIT' ? s.typeToggleIn : {})}}>IN</button>
                        )}
                        <button onClick={() => setDraft({...draft, transaction_type: 'DEBIT'})} style={{...s.typeToggle, ...(draft.transaction_type === 'DEBIT' ? s.typeToggleOut : {})}}>OUT</button>
                      </div>
                    </td>
                    <td style={s.td}>
                      <SearchableSelect options={categories} value={draft.ledger_category_id || ''} onChange={val => setDraftField('ledger_category_id', val)} onAddNew={addNewCategory} placeholder="Select category..." addNewLabel="category"
                        onRenameOption={renameCategory}
                        onDeleteOption={opt => openDelete({ kind: 'category', option: opt })} />
                    </td>
                    <td style={s.td}>
                      <SearchableSelect options={subcategoriesForLedger} value={draft.ledger_subcategory_id || ''} onChange={val => setDraftField('ledger_subcategory_id', val)} onAddNew={addNewSubcategory}
                        placeholder={draft.ledger_category_id ? 'Select subledger...' : 'Pick a ledger first'}
                        disabled={!draft.ledger_category_id}
                        addNewLabel={parentNameForSlot(1) ? `to ${parentNameForSlot(1)}` : 'subcategory'}
                        onRenameOption={renameSubcategory}
                        onDeleteOption={opt => openDelete({ kind: 'subcategory', option: opt })} />
                    </td>
                    {activeSlots.map(slot => {
                      const field = `ledger_subledger_${slot}_id`;
                      // Slot 2 hangs off Subledger 1; 3-5 off the slot above.
                      const parentId = slot === 2
                        ? draft.ledger_subcategory_id
                        : draft[`ledger_subledger_${slot - 1}_id`];
                      const parentName = parentNameForSlot(slot);
                      return (
                        <td key={slot} style={s.td}>
                          <SearchableSelect options={subledgerValuesForParent[slot] || []} value={draft[field] || ''} onChange={val => setDraftField(field, val)} onAddNew={addNewSubledgerValueFor(slot)}
                            placeholder={parentId ? `Subledger ${slot}...` : `Pick Subledger ${slot - 1} first`}
                            disabled={!parentId}
                            addNewLabel={parentName ? `to ${parentName}` : `Subledger ${slot} value`}
                            onRenameOption={renameSubledgerValue(slot)}
                            onDeleteOption={opt => openDelete({ kind: 'subledger', slot, option: opt })} />
                        </td>
                      );
                    })}
                    {canAddSubledger && <td style={s.td} />}
                    <td style={s.td}>
                      <select style={s.input} value={draft.payment_mode || 'UPI'} onChange={e => setDraft({...draft, payment_mode: e.target.value})}>
                        <option value="UPI">UPI</option><option value="CASH">Cash</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CARD">Card</option>
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.3rem', fontSize: '0.7rem', color: '#475569', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!draft.paid_from_petty_cash} onChange={e => setDraft({...draft, paid_from_petty_cash: e.target.checked})} />
                        Petty cash
                      </label>
                    </td>
                    {/* Text, not number: an <input type="number"> drops a
                        leading "+" before it ever reaches onChange, so typing
                        +500 to mark a return would be silently swallowed.
                        inputMode keeps the numeric keypad on mobile. */}
                    <td style={s.td}>
                      <input
                        type="text"
                        inputMode="decimal"
                        style={{...s.input, textAlign: 'center'}}
                        placeholder="0.00"
                        title="A leading + records the entry as a return (money coming back)"
                        value={draft.amount ?? ''}
                        onChange={e => setDraft({ ...draft, ...amountDraftPatch(e.target.value, draft) })}
                      />
                    </td>
                    <td style={s.td}>
                      <SearchableSelect options={transferredFromOptions} value={draft.transferred_from_id || ''} onChange={val => setDraft({...draft, transferred_from_id: val})} onAddNew={addNewTransferredFrom} placeholder="Transferred from..." addNewLabel="option"
                        onRenameOption={renameTransferredFrom}
                        onDeleteOption={deleteTransferredFrom} />
                    </td>
                    <td style={s.td}>
                      <SearchableSelect options={senderOptions} value={draft.sender_id || ''} onChange={val => setDraft({...draft, sender_id: val})} onAddNew={addNewSender} placeholder="Sender..." addNewLabel="sender"
                        onRenameOption={renameSender}
                        onDeleteOption={deleteSender} />
                    </td>
                    <td style={s.td}>
                      <SearchableSelect options={receiverOptions} value={draft.receiver_id || ''} onChange={val => setDraft({...draft, receiver_id: val})} onAddNew={addNewReceiver} placeholder="Receiver..." addNewLabel="receiver"
                        onRenameOption={renameReceiver}
                        onDeleteOption={deleteReceiver} />
                    </td>
                    <td style={s.td}>
                      {(() => {
                        const attached = parseProofs(draft.proof);
                        const room = MAX_PROOFS - attached.length;
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', justifyContent: 'center' }}>
                            {attached.map((url, i) => (
                              <span key={url + i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '0.1rem 0.3rem' }}>
                                <a href={resolveFileUrl(url)} target="_blank" rel="noopener noreferrer" title={url} style={{ color: '#16a34a', fontSize: '0.7rem', fontWeight: 700, textDecoration: 'none' }}>
                                  {isImageUrl(url) ? '🖼' : '📄'}{i + 1}
                                </a>
                                <button
                                  onClick={() => setDraft({ ...draft, proof: serializeProofs(attached.filter((_, j) => j !== i)) })}
                                  style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: 0, lineHeight: 1 }}
                                  title="Remove file"
                                >&times;</button>
                              </span>
                            ))}
                            {room > 0 && (
                              <label style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', display: 'inline-block' }}
                                title={`Attach up to ${MAX_PROOFS} files — select several at once`}>
                                {uploading ? '...' : attached.length ? `+ Add (${attached.length}/${MAX_PROOFS})` : 'Upload'}
                                {/* One button, several files: the API takes one file per call, so upload them in turn. */}
                                <input type="file" multiple style={{ display: 'none' }} disabled={uploading}
                                  onChange={async (e) => {
                                    const picked = Array.from(e.target.files || []);
                                    e.target.value = '';   // let the same file be re-picked after a removal
                                    if (picked.length === 0) return;
                                    const usable = picked.slice(0, room);
                                    if (picked.length > room) {
                                      toast.error(`Only ${room} more file(s) can be added (max ${MAX_PROOFS}).`);
                                    }
                                    setUploading(true);
                                    const uploaded: string[] = [];
                                    try {
                                      for (const file of usable) {
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        const res = await axios.post(`${API}/api/financial/upload-proof`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
                                        uploaded.push(res.data.proof_url);
                                      }
                                      toast.success(uploaded.length > 1 ? `${uploaded.length} files attached` : 'File attached');
                                    } catch {
                                      toast.error(uploaded.length ? 'Some files failed to upload' : 'Upload failed');
                                    } finally {
                                      // Keep whatever did upload rather than discarding the whole batch,
                                      // and merge functionally — other cells may have been edited while
                                      // the uploads were in flight.
                                      if (uploaded.length) {
                                        setDraft((d: any) => ({ ...d, proof: serializeProofs([...parseProofs(d.proof), ...uploaded]) }));
                                      }
                                      setUploading(false);
                                    }
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={s.td}>
                      {/* A picker over the REMARK list, but the column stays free
                          text: typing a one-off reason still saves, and the DEBIT
                          compliance rule (10+ chars) is unchanged — the red border
                          below still marks a row that does not satisfy it. */}
                      <div style={{ border: needsRemark ? '1px solid #ef4444' : undefined, borderRadius: needsRemark ? '6px' : undefined, backgroundColor: needsRemark ? '#fef2f2' : undefined }}>
                        <SearchableSelect
                          options={remarkSelectOptions}
                          value={draft.remarks || ''}
                          onChange={val => setDraft({ ...draft, remarks: val })}
                          onAddNew={addNewRemark}
                          placeholder={needsRemark ? 'Reason required...' : 'Notes...'}
                          addNewLabel="remark"
                          onRenameOption={renameRemarkOpt}
                          onDeleteOption={deleteRemarkOpt}
                          allowClear
                        />
                      </div>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
                        <button onClick={requestCloseEdit} style={{ background: 'white', border: '1px solid #cbd5e1', color: '#475569', padding: '0.35rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        <button onClick={saveEntry} disabled={uploading} title={uploading ? 'Waiting for the file upload to finish…' : 'Save'} style={{ background: uploading ? '#93c5fd' : '#2563eb', border: '1px solid #1d4ed8', color: 'white', padding: '0.35rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>{uploading ? 'Uploading…' : 'Save'}</button>
                      </div>
                    </td>
                  </tr>
                );
              })()}

              {/* ── LOADING STATE ── */}
              {loading && (
                <tr><td colSpan={tableColumnCount} style={s.emptyState}>Loading transactions…</td></tr>
              )}

              {/* ── READ-ONLY ROWS ── */}
              {!loading && sortedTransactions.map((tx, index) => {
                if (editingId === tx.id) return null;

                // Every cell always prints its real value — ditto marks read as
                // broken empty cells once a ledger spans dozens of rows. Group
                // boundaries are carried by the border rules below instead.
                const prev = index > 0 ? sortedTransactions[index - 1] : null;
                const prevKeys = prev ? groupKeysOf(prev) : null;
                const keys = groupKeysOf(tx);
                // sameThrough[i] — this row matches the previous one at every level up to i.
                const sameThrough = keys.map((_, i) =>
                  prevKeys !== null && keys.slice(0, i + 1).every((k, j) => k === prevKeys[j]));
                const sameDate = !!prev && prev.transaction_date === tx.transaction_date;
                // The "+" button would repeat on every row otherwise; it belongs
                // once per group, on the row that opens it.
                // No groups while searching or in SL No. order, so no group
                // heads — the "+" would otherwise land on whichever row
                // happened to sort first.
                const isGroupHead = ungrouped
                  ? false
                  : groupBy === 'ledger' ? !sameThrough[0] : !(sameDate && sameThrough[0]);
                const isCredit = tx.transaction_type === 'CREDIT';
                const isHovered = hoveredRowId === tx.id;
                const isSelected = selectedIds.has(tx.id);
                // Spread last into every cell's style so it wins over that
                // cell's own colour (muted SL No., red/green amount). Empty
                // for petty-cash rows, which keep the default palette.
                const rowTint: React.CSSProperties = tx.paid_from_petty_cash ? {} : { color: NON_PETTY_COLOR };

                // Searching orders by relevance and SL No. by serial, neither
                // of which follows the groups, so the group rules are
                // suppressed — they'd otherwise draw boundaries around runs
                // that no longer mean anything.
                const groupRule =
                  index === 0 || ungrouped ? 'none'
                  : groupBy === 'date'
                    ? (!sameDate ? `2px solid ${colors.border}` : 'none')
                  : !sameThrough[0] ? `2px solid ${colors.border}`        // new ledger
                  : !sameThrough[1] ? `1px solid ${colors.border}`        // new subledger 1
                  : 'none';

                return (
                  <tr
                    key={tx.id}
                    onMouseDown={onRowMouseDown(index)}
                    onMouseEnter={onRowMouseEnter(index)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onClick={() => {
                      // A drag that ended on this row isn't a click to edit.
                      if (didDrag.current) return;
                      if (selectedIds.size > 0) setSelectedIds(new Set());
                      guardSwitch(() => startEdit(tx));
                    }}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? colors.primaryBg2 : isHovered ? colors.primaryBg : 'transparent',
                      borderTop: groupRule,
                    }}
                  >
                    <td style={{...s.td, color: colors.textMuted, fontVariantNumeric: 'tabular-nums', ...rowTint}}>
                      {tx.ledger_no ?? '—'}
                    </td>
                    <td style={{...s.td, fontWeight: 500, ...rowTint}}>
                      {tx.transaction_date
                        ? new Date(tx.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '-'}
                    </td>
                    <td style={{...s.td, fontWeight: 600, ...rowTint}} title={tx.ledger_category_name || undefined}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing(1) }}>
                        {tx.ledger_category_name || '-'}
                        {isGroupHead && (
                          <button
                            onClick={e => { e.stopPropagation(); guardSwitch(() => startNewEntryFor(tx)); }}
                            title="Add another entry under this ledger"
                            style={s.addRowBtn}
                          >+</button>
                        )}
                      </span>
                    </td>
                    <TextCell value={tx.ledger_subcategory_name} style={rowTint} />
                    {activeSlots.map(slot => (
                      <TextCell key={slot} value={tx[`ledger_subledger_${slot}_name`]} style={rowTint} />
                    ))}
                    {canAddSubledger && <td style={s.td} />}
                    <TextCell value={tx.payment_mode} style={rowTint} />
                    <td style={{ ...s.tdRight, color: isCredit ? colors.success : colors.danger, ...rowTint }}>
                      {isCredit ? '+' : '-'} ₹{parseFloat(tx.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <TextCell value={tx.transferred_from_name} style={rowTint} />
                    <TextCell value={tx.sender_name} style={rowTint} />
                    <TextCell value={tx.receiver_name} style={rowTint} />
                    <td style={{ ...s.td, padding: '0.3rem 0.5rem' }}>
                      <ProofCell value={tx.proof} caption={tx.ledger_no ? `Ledger #${tx.ledger_no}` : undefined} />
                    </td>
                    <TextCell value={tx.remarks} placeholder="—" style={{ fontSize: '0.8rem', ...rowTint }} />
                    <td style={s.td}>
                      <div style={{ display: 'inline-flex', gap: '4px' }}>
                        <button onClick={e => { e.stopPropagation(); guardSwitch(() => startEdit(tx)); }} style={s.actionBtnEdit}>Edit</button>
                        <button onClick={e => { e.stopPropagation(); deleteEntry(tx.id); }} style={s.actionBtnDelete}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && sortedTransactions.length === 0 && editingId !== 'new' && (
                <tr>
                  <td colSpan={tableColumnCount} style={s.emptyState}>
                    {transactions.length === 0 ? 'No transactions found for this period.' : 'No transactions match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </SizedTable>
          </ResizableColumnsProvider>
      </div>

      {/* The table's own horizontal scrollbar sits below the fold on any real
          month of data — this mirrors it at the bottom of the screen. */}
      <FloatingHScrollbar targetRef={tableWrapRef} />

      {/* ── UNSAVED CHANGES PROMPT ── */}
      {unsavedPrompt && (
        <div style={s.modalOverlay} data-ledger-modal>
          <div style={s.modalCard}>
            <h3 style={s.modalTitle}>Save your changes?</h3>
            <p style={s.modalBody}>
              This {editingId === 'new' ? 'new entry has' : 'entry has'} unsaved changes. Saving runs the
              usual checks — a debit still needs a proof or a reason.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing(2), marginTop: spacing(4) }}>
              <button onClick={() => setUnsavedPrompt(false)} style={s.btnGhost}>Keep editing</button>
              <button
                onClick={closeEdit}
                style={{ ...s.btnGhost, color: colors.danger, borderColor: colors.danger }}
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  // saveEntry closes the editor itself on success; on failure it
                  // toasts and returns false, so drop the prompt and leave the
                  // row open with the problem still on screen.
                  const ok = await saveEntry();
                  if (!ok) setUnsavedPrompt(false);
                }}
                style={{ background: colors.primary, border: 'none', color: 'white', padding: '0.5rem 1rem', borderRadius: radius.sm, fontSize: fontSize.md, fontWeight: fontWeight.semibold, cursor: 'pointer' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE NAME CONFIRMATION ── */}
      {deleteTarget && (() => {
        const isCategory = deleteTarget.kind === 'category';
        const count = deleteUsage?.count ?? 0;
        return (
          <div style={s.modalOverlay} data-ledger-modal onClick={() => !deleting && setDeleteTarget(null)}>
            <div style={s.modalCard} onClick={e => e.stopPropagation()}>
              <h3 style={s.modalTitle}>Delete {DELETE_LABEL[deleteTarget.kind]}</h3>
              <p style={s.modalBody}>
                {isCategory
                  ? <>Deleting <strong>“{deleteTarget.option.name}”</strong> permanently removes the ledger name{count > 0 ? <> <strong>and all {count} {count === 1 ? 'entry' : 'entries'}</strong> recorded under it</> : ''}. This cannot be undone.</>
                  : <>Deleting <strong>“{deleteTarget.option.name}”</strong> removes this tag{count > 0 ? <> from <strong>{count} {count === 1 ? 'entry' : 'entries'}</strong></> : ''}. The {count === 1 ? 'entry keeps' : 'entries keep'} their amount and date — only this label is cleared.</>}
              </p>

              {deleteUsageLoading ? (
                <div style={s.modalSampleBox}>Checking where it’s used…</div>
              ) : count > 0 ? (
                <div style={s.modalSampleBox}>
                  {deleteUsage!.sample.map((e: any) => (
                    <div key={e.id} style={s.modalSampleRow}>
                      <span>{e.transaction_date ? new Date(e.transaction_date).toLocaleDateString('en-GB') : '—'}</span>
                      <span style={{ flex: 1, margin: '0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.particular || '—'}</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>₹{parseFloat(e.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  {count > deleteUsage!.sample.length && (
                    <div style={{ ...s.modalSampleRow, color: colors.textMuted, justifyContent: 'center' }}>
                      +{count - deleteUsage!.sample.length} more
                    </div>
                  )}
                </div>
              ) : (
                <div style={s.modalSampleBox}>Not used by any entries.</div>
              )}

              {isCategory && (
                <div style={{ marginTop: spacing(3) }}>
                  <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Enter your password to confirm</label>
                  <input
                    type="password"
                    style={{ ...s.input, marginTop: spacing(1) }}
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    placeholder="Password"
                  />
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing(2), marginTop: spacing(4) }}>
                <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={s.btnGhost}>Cancel</button>
                <button onClick={confirmDelete} disabled={deleting || deleteUsageLoading} style={{ ...s.actionBtnDelete, padding: '0.5rem 1rem', fontSize: fontSize.md, opacity: deleting || deleteUsageLoading ? 0.6 : 1 }}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete a single entry — password-gated on the server. */}
      {deleteEntryTarget && (
        <div style={s.modalOverlay} data-ledger-modal onClick={() => !deletingEntry && setDeleteEntryTarget(null)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Delete entry</h3>
            <p style={s.modalBody}>
              This permanently removes the entry from the ledger and the P&amp;L. This cannot be undone.
            </p>
            <div style={s.modalSampleBox}>
              <div style={s.modalSampleRow}>
                <span>{deleteEntryTarget.transaction_date ? new Date(deleteEntryTarget.transaction_date).toLocaleDateString('en-GB') : '—'}</span>
                <span style={{ flex: 1, margin: '0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {deleteEntryTarget.particular || '—'}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  ₹{parseFloat(deleteEntryTarget.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              {(deleteEntryTarget.ledger_category_name || deleteEntryTarget.ledger_subcategory_name) && (
                <div style={{ ...s.modalSampleRow, color: colors.textMuted }}>
                  {[deleteEntryTarget.ledger_category_name, deleteEntryTarget.ledger_subcategory_name,
                    ...[2, 3, 4, 5].map(n => deleteEntryTarget[`ledger_subledger_${n}_name`])]
                    .filter(Boolean).join(' / ')}
                </div>
              )}
            </div>
            <div style={{ marginTop: spacing(3) }}>
              <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Enter your password to confirm</label>
              <input
                autoFocus
                type="password"
                style={{ ...s.input, marginTop: spacing(1) }}
                value={deleteEntryPassword}
                onChange={e => setDeleteEntryPassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmDeleteEntry(); }}
                placeholder="Password"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing(2), marginTop: spacing(4) }}>
              <button onClick={() => setDeleteEntryTarget(null)} disabled={deletingEntry} style={s.btnGhost}>Cancel</button>
              <button
                onClick={confirmDeleteEntry}
                disabled={deletingEntry || deleteEntryPassword === ''}
                style={{ ...s.actionBtnDelete, padding: '0.5rem 1rem', fontSize: fontSize.md, opacity: deletingEntry || !deleteEntryPassword ? 0.6 : 1 }}
              >
                {deletingEntry ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReorderLedgersModal
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
        categories={categories}
        onSaved={fetchAllData}
      />

      <LedgerDownloadDialog
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        month={month}
        year={year}
        categories={categories}
        subcategories={subcategories}
      />
    </div>
  );
}